import React, { useState, useEffect, useRef } from 'react'
import { router, Link, Head, usePage } from '@inertiajs/react'
import { ArrowLeft, Plus, ChevronLeft, ChevronRight, CalendarDays, Copy, X as XIcon, Printer, XCircle, Undo2, Ban, Search, PhoneCall, UserPlus, Check, Clock, Bell, Mic, MicOff, FileText, CalendarPlus } from 'lucide-react'
import { toast } from 'sonner'
import AppointmentDetailModal from '../components/AppointmentDetailModal'
import AppointmentFormModal from '../components/AppointmentFormModal'
import CancelAppointmentModal, { LOSS_CAUSES } from '../components/CancelAppointmentModal'
import DiaryContextMenu from '../components/DiaryContextMenu'

// Friendly status labels for the right-click menu's success toast.
const STATUS_TOAST = {
  pending_confirmation: 'Marked as awaiting confirmation',
  confirmed: 'Confirmed',
  arrived: 'Marked as arrived',
  in_consultation: 'Consultation started',
  completed: 'Marked as completed',
}

// ── Elixir-style day diary: one column per dentist ────────────────────
// Matches the practice's Elixir "express diary": a time axis down the left,
// a column per working dentist, blocks showing NAME [ACCOUNT] + reason,
// coloured by type (green treatment · purple consult · yellow new-patient),
// pink "Closed" blocks for breaks. Ivory appointments are editable; the
// read-only Elixir history for the same day is shown faded for context.
const DAY_START = 6 * 60   // 06:00 — diary DISPLAYS from 6am so reception can place + see EARLY manual
                           // appointments (Paul 2026-08-05: a 6am booking existed but sat off-grid).
                           // AI/patient booking still follows each provider's scheduled hours.
const DAY_END = 19 * 60    // 19:00
const PX = 1.4             // pixels per minute (slightly taller rows so 15-min slots read clearly)

const minsFromMidnight = (iso) => {
  const d = new Date(iso)
  return d.getHours() * 60 + d.getMinutes()
}
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })
// First-visit year (Paul 2026-08-05): the year the patient's FIRST invoice was issued, shown right after
// their account number so reception can spot patients who predate the 2026 locum (Dr Eliska) at a glance —
// those are Dr Chalita's own patients and must stay with her (turnover). A pre-2026 year gets an amber pill
// so it jumps out; a 2026 first-year (a genuinely new patient) is shown muted. Absent = never invoiced.
const FirstVisitYear = ({ year }) =>
  year ? (
    <span
      title={year < 2026
        ? `First invoice ${year} — existing patient, predates the locum (keep with Dr Chalita)`
        : `First invoice ${year} — new patient`}
      className={`ml-1 shrink-0 rounded px-1 text-[9px] font-bold leading-none ${
        year < 2026 ? 'bg-amber-200 text-amber-900' : 'bg-slate-200/80 text-slate-500'
      }`}
    >
      {year}
    </span>
  ) : null
// ID check due (2026-09-03): the patient e-signed their intake pack at home — the code they entered
// proved they hold the phone/mailbox on file, not who they are — and no named person at the desk has
// checked their ID yet. Small amber "ID" pill on the tile so it gets done while they are standing there;
// the action itself is on the patient page (S-3 #6 / A-7). Absent = nothing signed, or already checked.
const IdCheckDue = ({ due }) =>
  due ? (
    <span
      title="Signed their intake pack electronically — check their ID when they arrive and confirm it on the patient page"
      className="ml-1 shrink-0 rounded bg-amber-500 px-1 text-[9px] font-bold leading-none text-white"
    >
      ID
    </span>
  ) : null
// Last-invoice dentist (Paul 2026-09-02): "put a C or E if their previous invoice was Dr Chalita (C) or
// Dr Eliska (E)". Sits right after the year: C/E in that dentist's own diary colour, so reception books a
// returning patient with whoever actually treated them last. A former locum (not a current provider)
// shows grey two-letter initials — never a bare "E" that could be mistaken for Dr Eliska. Absent = never
// invoiced. Payload: { letter, name, color, date } from LastInvoiceDentistProvider.
// Screen-only (Paul 2026-09-08): the C/E last-invoice-dentist pill is internal — `no-print` hides it on a
// browser print of the diary, and the Print schedule page (appointments/print_schedule.html.erb) never renders it.
const LastDentist = ({ d }) =>
  d?.letter ? (
    <span
      title={`Last invoice: ${d.name}${d.date ? ` · ${new Date(d.date + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`}
      className={`no-print ml-0.5 shrink-0 rounded px-1 text-[9px] font-bold leading-none ${d.color ? 'text-white' : 'bg-slate-300 text-slate-700'}`}
      style={d.color ? { backgroundColor: d.color } : undefined}
    >
      {d.letter}
    </span>
  ) : null
const fmtDayTitle = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-ZA', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
// Pure date math in UTC so a +2 (SAST) browser doesn't roll the day back when we
// round-trip through toISOString(). (This was why the prev/next arrows "didn't move".)
const shiftDate = (dateStr, n) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}
const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
// Shift by whole months, clamping the day so 31 Jan + 1mo → 28/29 Feb (UTC, like shiftDate).
const shiftMonth = (dateStr, n) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  const target = new Date(Date.UTC(y, m - 1 + n, 1))
  const ty = target.getUTCFullYear(), tm = target.getUTCMonth()
  const daysInTarget = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate()
  return new Date(Date.UTC(ty, tm, Math.min(d, daysInTarget))).toISOString().slice(0, 10)
}
// One step for the prev/next arrows, by the chosen unit (Paul 2026-06-26 — quick week/month nav).
const shiftByUnit = (dateStr, dir, unit) =>
  unit === 'month' ? shiftMonth(dateStr, dir)
    : unit === 'week' ? shiftDate(dateStr, dir * 7)
      : shiftDate(dateStr, dir)

// Popover month-calendar for the diary header. Keeps its OWN displayed-month state, so paging
// months never navigates the diary or closes the popover — fixes the native <input type=date>
// being closed by the 30s live-refresh ("going to the next month throws me out", Paul 2026-06-26).
// Picking a day navigates the diary.
function MiniCalendar({ value, onPick, onClose }) {
  const base = value || todayStr()
  const [vy, setVy] = useState(Number(base.slice(0, 4)))
  const [vm, setVm] = useState(Number(base.slice(5, 7)) - 1)
  const ref = useRef(null)
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDoc)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [onClose])
  const first = new Date(Date.UTC(vy, vm, 1))
  const startDow = (first.getUTCDay() + 6) % 7 // Monday-first
  const days = new Date(Date.UTC(vy, vm + 1, 0)).getUTCDate()
  const label = first.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  const step = (n) => { const d = new Date(Date.UTC(vy, vm + n, 1)); setVy(d.getUTCFullYear()); setVm(d.getUTCMonth()) }
  const iso = (d) => `${vy}-${String(vm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const cells = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= days; d++) cells.push(d)
  return (
    <div ref={ref} className="absolute left-0 top-full z-50 mt-2 w-64 rounded-xl border border-brand-border bg-white p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" onClick={() => step(-1)} aria-label="Previous month" className="rounded p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronLeft size={16} /></button>
        <span className="text-sm font-semibold text-brand-ink">{label}</span>
        <button type="button" onClick={() => step(1)} aria-label="Next month" className="rounded p-1 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronRight size={16} /></button>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium text-brand-muted">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />
          const day = iso(d)
          const cls = day === value ? 'bg-brand-primary text-white'
            : day === todayStr() ? 'font-bold text-brand-primary hover:bg-brand-primary/10'
              : 'text-brand-ink hover:bg-brand-primary/10'
          return <button key={day} type="button" onClick={() => onPick(day)} className={`rounded py-1 text-xs ${cls}`}>{d}</button>
        })}
      </div>
    </div>
  )
}

// Block colour follows the patient-journey status (Paul's workflow).
const WHITE_TEXT = new Set(['in_consultation', 'completed'])
const apptStyle = (a) => {
  const hex = a.status_color || '#ffffff'
  const isWhite = hex.toLowerCase() === '#ffffff'
  return {
    backgroundColor: hex,
    color: WHITE_TEXT.has(a.status) ? '#ffffff' : '#1f2937',
    border: isWhite ? '1px solid #cbd5e1' : `1px solid rgba(0,0,0,0.12)`,
    borderLeft: `4px solid ${isWhite ? '#94a3b8' : hex}`,
  }
}

const CANCEL_LABELS = {
  patient_request: 'Patient requested', not_needed: 'No longer needed', rescheduling: 'Rescheduling',
  diary_error: 'Diary error / double-booking', dentist_unavailable: 'Dentist unavailable',
  practice_closed: 'Practice closed', no_response: 'No response to confirmation',
  cost: 'Cost', timing: 'Timing', fear: 'Anxiety', transport: 'Transport', other: 'Other',
}
const CANCEL_BY = { patient: 'patient', reception: 'reception', practice: 'us', dentist: 'dentist', system: 'system' }

// ── 16:00 ESTIMATES REMINDER (Paul 2026-09-04) ──────────────────────────────────────────────────────
// "Maybe leave a pop-up reminder for estimates from 4 pm if the day still requires estimates to be done."
// From REMINDER_HOUR on TODAY's diary, a DENTIST login (never the owner/reception) whose column still has visits marked
// "estimate to do" gets ONE pop-up listing them, each with a one-click "Start estimate" (the same action as
// the visit modal's button). "Remind me in 30 min" snoozes; "Done for today" is quiet until tomorrow (per
// user, per day, in this browser only). Re-evaluated every minute so a diary left open crosses 16:00 on its
// own. Dentist-only data: it reads the card's estimate_status, which the server already scopes to their
// column — it never carries amounts (those stay owner/reception only).
const REMINDER_HOUR = 16
const reminderKey = (date, uid) => `ivory:estimate-reminder:${date}:${uid || 'anon'}`
const readSnooze = (date, uid) => { try { return Number(localStorage.getItem(reminderKey(date, uid)) || 0) } catch { return 0 } }
function EstimateReminder({ date, appointments = [], cu, fmtTime }) {
  const eligibleRole = cu?.role === 'dentist'   // dentists only — not the owner's login (Paul 2026-09-04)
  const [, setTick] = useState(0)
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60_000); return () => clearInterval(t) }, [])
  if (!eligibleRole || date !== todayStr()) return null
  if (new Date().getHours() < REMINDER_HOUR) return null
  const todo = appointments.filter((a) => a.estimate_status === 'needed' && ['completed', 'in_consultation', 'arrived'].includes(a.status))
  if (todo.length === 0) return null
  if (readSnooze(date, cu?.id) > Date.now()) return null
  const snooze = (ms) => { try { localStorage.setItem(reminderKey(date, cu?.id), String(Date.now() + ms)) } catch {} ; setTick((n) => n + 1) }
  return (
    /* A DOCKED side panel, not a blocking pop-up (Paul 2026-09-04): bottom-right, no backdrop, so the dentist keeps
       seeing and working the diary behind it. Only the panel itself catches clicks. */
    <div className="pointer-events-none fixed inset-y-0 right-0 z-40 flex items-end p-4">
      <div className="pointer-events-auto w-80 max-w-[90vw] rounded-xl border border-amber-200 bg-white p-4 shadow-2xl ring-1 ring-amber-300/60">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-brand-ink">Estimates still to do today · {todo.length}</h3>
          <button type="button" onClick={() => snooze(30 * 60_000)} title="Hide for 30 minutes" className="rounded p-0.5 text-brand-muted hover:bg-slate-100 hover:text-brand-ink">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-brand-muted">After {REMINDER_HOUR}:00 — still marked "estimate to do".</p>
        <ul className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
          {todo.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-brand-border px-2.5 py-1.5 text-xs">
              <span className="min-w-0 truncate">
                <span className="font-medium text-brand-ink">{a.patient_name}</span>
                <span className="ml-1 text-xs text-brand-muted">{fmtTime ? fmtTime(a.start_time) : ''}{a.reason ? ` · ${a.reason}` : ''}</span>
              </span>
              <button type="button" onClick={() => router.post(`/patients/${a.patient_id}/estimates`)}
                      className="shrink-0 rounded-md bg-brand-primary px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90">
                Start estimate
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" onClick={() => snooze(30 * 60_000)} className="rounded-md border border-brand-border px-2.5 py-1 text-[11px] font-medium text-brand-muted hover:bg-slate-50">Remind me in 30 min</button>
          <button type="button" onClick={() => snooze(12 * 3_600_000)} className="rounded-md bg-brand-ink px-2.5 py-1 text-[11px] font-semibold text-white">Done for today</button>
        </div>
      </div>
    </div>
  )
}

export default function Diary({
  date,
  providers = [],
  diary_nudges = [],
  appointments = [],
  cancelled_appointments = [],
  elixir_blocks = [],
  closed_blocks = [],
  schedule_closed_blocks = [],
  schedule_overrides = [],
  waitlist = [],
  doctor_letters = [],
  to_book = [],
  patients = [],
}) {
  // A provider-scoped associate (a NON-admin linked to a provider, e.g. Dr Eliska -> provider 2) keeps
  // FULL dentist abilities (create/edit/cancel) but is confined to their OWN provider: the diary READS
  // are scoped server-side (AppointmentsController#scoped_provider_ids) and every WRITE is force-pinned
  // to their own provider server-side too. The frontend adds defence-in-depth: lock the dentist picker
  // (view filter + the create/edit form) to their provider, so a new booking defaults to THEM and can't
  // be aimed at another dentist.
  const cu = usePage().props?.current_user
  const scopedProviderId = (cu && cu.role !== 'admin' && cu.provider_id != null) ? String(cu.provider_id) : null
  const scoped = scopedProviderId !== null
  // Read-only "viewer" staff: the diary is look-only. A scoped associate is NOT view-only — they book and
  // manage their own day; the provider lock (below) + the server-side write-scope keep them on their own diary.
  const readOnly = cu?.role === 'viewer'
  const [modalMode, setModalMode] = useState(null)
  const [selected, setSelected] = useState(null)
  const [prefillStart, setPrefillStart] = useState(null)
  const [prefillProvider, setPrefillProvider] = useState(null)
  const [startBlocked, setStartBlocked] = useState(false)   // open the create form straight into block-out mode
  const [selectedSlot, setSelectedSlot] = useState(null)    // { providerId, startMin } — highlighted empty slot (Ctrl+V target)
  const [slotMenu, setSlotMenu] = useState(null)            // { x, y, provider, start } — right-click menu on empty space
  const [prefillDuration, setPrefillDuration] = useState(null) // minutes, when the create form is opened by a DRAG-to-size
  const [createDrag, setCreateDrag] = useState(null)        // { providerId, startMin, endMin } — live drag-to-create preview
  const createDragRef = useRef(null)
  const suppressClickRef = useRef(false)                    // a drag-create ends in a mouseup that would fire the column's select-onClick — swallow it

  const closeModal = () => { setModalMode(null); setSelected(null); setPrefillStart(null); setPrefillProvider(null); setStartBlocked(false); setPrefillDuration(null) }

  // Which dentist's schedule to show — 'all' (every column) or a single provider id. Persisted in
  // sessionStorage so it survives day navigation (which remounts this page).
  const [viewProviderId, setViewProviderId] = useState(() => {
    if (scoped) return scopedProviderId // locked to the associate's own provider — never "all"
    try { return sessionStorage.getItem('diary:viewProvider') || 'all' } catch { return 'all' }
  })
  const setViewProvider = (v) => {
    setViewProviderId(v)
    try { (v && v !== 'all') ? sessionStorage.setItem('diary:viewProvider', v) : sessionStorage.removeItem('diary:viewProvider') } catch { /* ignore */ }
  }
  const filteredProviders = viewProviderId === 'all' ? providers : providers.filter((p) => String(p.id) === String(viewProviderId))
  // If the saved filter points at a dentist not in today's list, fall back to showing all.
  const effectiveProviders = filteredProviders.length ? filteredProviders : providers
  const openDetail = (apt) => { setSelected(apt); setModalMode('detail') }
  const openEdit = (apt) => { if (apt) setSelected(apt); setModalMode('edit') }
  const openCancel = (apt) => { if (apt) setSelected(apt); setModalMode('cancel') }

  // Delete = PERMANENTLY remove from the diary (hard delete, not the soft grey-out cancel).
  // Confirmed because it's irreversible. Wired to the modal "Delete" button, the right-click
  // "Delete" item, and the keyboard Delete/Backspace key on the selected block.
  const deleteAppointment = (apt) => {
    if (!apt) return
    if (!window.confirm(`Permanently delete ${apt.patient_name || 'this'} appointment? It will be removed from the diary and cannot be undone.`)) return
    // 📉 WHICH removal is this? (lossreasons1014) — required: a diary correction, or a patient who isn't coming (then WHY,
    // with the same causes as the cancel dialog, and the patient's own words — required for "Other").
    const correction = window.confirm('Why is this coming out of the diary?\n\nOK = Diary correction (duplicate / wrong slot)\nCancel = Patient isn\u2019t coming')
    let removal = { removal_kind: 'diary_correction' }
    if (!correction) {
      const menu = LOSS_CAUSES.map((c, i) => `${i + 1}. ${c.label}`).join('\n')
      const pick = window.prompt(`Why isn\u2019t the patient coming? Type the number:\n\n${menu}`, '')
      const cause = LOSS_CAUSES[parseInt(pick, 10) - 1]
      if (!cause) { toast.error('Pick why the patient isn\u2019t coming (or \u2018Patient didn\u2019t say\u2019).'); return }
      let words = ''
      if (cause.value !== 'not_said') {
        words = window.prompt(`The patient\u2019s own words${cause.value === 'other' ? ' (required)' : ' (optional)'} — e.g. \u2018my medical aid only pays next year\u2019`, '') || ''
        if (cause.value === 'other' && !words.trim()) { toast.error('Add the patient\u2019s own words for \u2018Other\u2019.'); return }
      }
      removal = { removal_kind: 'patient_not_coming', cause: cause.value, patient_words: words }
    }
    // Ask WHETHER to tell the patient (Paul 2026-07-04): sometimes we free an unconfirmed slot quietly.
    const notify = window.confirm(`Send ${apt.patient_name || 'the patient'} a WhatsApp cancellation message?\n\nOK = Yes, tell them it's cancelled.\nCancel = No, remove it quietly (the patient won't know).`)
    router.delete(`/appointments/${apt.id}`, {
      data: { send_cancellation: notify, ...removal },
      preserveScroll: true,
      onSuccess: () => { toast(notify ? 'Appointment deleted — patient notified' : 'Appointment deleted quietly'); closeModal() },
      onError: () => toast.error('Could not delete'),
    })
  }

  // Restore a cancelled appointment back into the diary (status -> scheduled).
  const restoreAppt = (apt) => {
    if (!apt) return
    router.patch(`/appointments/${apt.id}/set_status`, { status: 'scheduled' }, {
      preserveScroll: true, preserveState: true,
      onSuccess: () => toast('Appointment restored to the diary'),
      onError: () => toast.error('Could not restore'),
    })
  }

  // Popover month picker open state. Date stepping uses three independent arrow pairs (day/week/month).
  const [pickerOpen, setPickerOpen] = useState(false)

  const goTo = (d) => router.get('/diary', { date: d }, { preserveScroll: true })

  // Live refresh every 30s so the diary stays current across machines — but NEVER
  // while a modal is open (it was interrupting clicks / making things feel stuck).
  // NOTE: ctxMenu is declared further down, so it must NOT be referenced here
  // (doing so crashed the whole page — temporal dead zone). The context menu
  // closes itself on outside-click/Escape, so pausing the 30s refresh for it
  // isn't needed.
  useEffect(() => {
    if (modalMode) return undefined
    const t = setInterval(() => {
      // 'providers' MUST be here so the live transcribing pulse (green mic / red mic-off) refreshes every
      // cycle — without it the mic froze at page-load state and stayed green after the mic went down (Paul 2026-08-11).
      router.reload({ only: ['appointments', 'elixir_blocks', 'closed_blocks', 'providers', 'diary_nudges'], preserveState: true, preserveScroll: true })
    }, 30_000)
    return () => clearInterval(t)
  }, [modalMode])

  // Click a historical Elixir block → pre-fill a new booking for that dentist + time
  // (so clicking history "re-books" the slot, and a click always opens something).
  const openRebook = (b) => {
    const prov = providers.find((p) => p.id === b.provider_id) || null
    setPrefillProvider(prov)
    setPrefillStart(new Date(b.start_time))
    setSelected(null)
    setModalMode('create')
  }

  // Click a "Closed" block to remove it.
  const removeClosed = (c) => {
    if (window.confirm(`Remove blocked time "${c.note || 'Closed'}"?`)) {
      router.delete(`/calendar_notes/${c.id}`, { preserveScroll: true })
    }
  }

  // Per-day schedule override (Paul 2026-08-21). Clicking a "Not working" band opens a small editor to
  // OPEN a working window on THIS day only (weekly template untouched). Reset reverts the day.
  const HHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
  const [scheduleEditor, setScheduleEditor] = useState(null) // { providerId, providerName, start, end, hasOverride }
  const openScheduleEditor = (prov, block) => {
    const startMin = Math.min(Math.max(minsFromMidnight(block.starts_at), DAY_START), DAY_END - 45)
    setScheduleEditor({
      providerId: prov.id,
      providerName: prov.name,
      start: HHMM(startMin),
      end: HHMM(Math.min(startMin + 45, DAY_END)),
      hasOverride: schedule_overrides.includes(prov.id),
    })
  }
  const submitScheduleOpen = () => {
    const ed = scheduleEditor
    if (!ed) return
    router.post('/diary/open-window',
      { provider_id: ed.providerId, date, start: ed.start, end: ed.end },
      { preserveScroll: true, onFinish: () => setScheduleEditor(null) })
  }
  const resetScheduleDay = (providerId) => {
    router.delete('/diary/reset-day',
      { data: { provider_id: providerId, date }, preserveScroll: true, onFinish: () => setScheduleEditor(null) })
  }

  const gridHeight = (DAY_END - DAY_START) * PX
  const hourRows = []
  for (let h = DAY_START / 60; h <= DAY_END / 60; h++) hourRows.push(h)
  // Every 15-min mark (the booking step), tagged by weight so the diary reads like Google/Outlook:
  // bold hour lines, lighter half-hour, faint dashed quarter-hour. (Perplexity-backed hierarchy.)
  const gridLines = []
  for (let m = DAY_START; m <= DAY_END; m += 15) {
    gridLines.push({ min: m, kind: m % 60 === 0 ? 'hour' : m % 30 === 0 ? 'half' : 'quarter' })
  }
  const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

  const blockBox = (startIso, endIso) => {
    const s = Math.max(minsFromMidnight(startIso), DAY_START)
    const e = Math.min(minsFromMidnight(endIso), DAY_END)
    return { top: (s - DAY_START) * PX, height: Math.max((e - s) * PX, 20) }
  }

  // Side-by-side layout for OVERLAPPING appointments (Paul 2026-07-05): when blocks overlap in a
  // column they used to stack full-width on top of each other (you couldn't see the one behind, or
  // watch one move past another while dragging). Narrow overlapping blocks and place them next to
  // each other — the standard calendar behaviour. Greedy interval lane-packing PER overlap cluster;
  // returns id -> { left, width } CSS percentages. A non-overlapping block stays effectively full width.
  const computeApptLanes = (list) => {
    const lay = new Map()
    const items = list.map((a) => ({ id: a.id, s: minsFromMidnight(a.start_time), e: minsFromMidnight(a.end_time) }))
                     .sort((x, y) => x.s - y.s || x.e - y.e)
    let cluster = [], clusterEnd = -1
    const flush = () => {
      if (!cluster.length) return
      const laneEnds = []
      cluster.forEach((it) => {
        let lane = laneEnds.findIndex((end) => it.s >= end)
        if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.e) } else { laneEnds[lane] = it.e }
        it.lane = lane
      })
      const cols = laneEnds.length
      const w = 100 / cols
      cluster.forEach((it) => lay.set(it.id, { left: `calc(${it.lane * w}% + 2px)`, width: `calc(${w}% - 4px)` }))
      cluster = []; clusterEnd = -1
    }
    items.forEach((it) => {
      if (cluster.length && it.s >= clusterEnd) flush()
      cluster.push(it); clusterEnd = Math.max(clusterEnd, it.e)
    })
    flush()
    return lay
  }

  // Compute the snapped 15-min slot { start: Date, startMin } from a click on an empty column.
  // Returns null if the click landed on a block.
  const slotFromEvent = (ev) => {
    if (ev.target.closest('[data-block]')) return null
    const rect = ev.currentTarget.getBoundingClientRect()
    const y = ev.clientY - rect.top
    const raw = DAY_START + Math.round((y / PX) / 15) * 15
    const snapped = Math.min(Math.max(raw, DAY_START), DAY_END - 15) // clamp to clinic hours
    const dt = new Date(date + 'T00:00:00')
    dt.setMinutes(snapped)
    return { start: dt, startMin: snapped }
  }

  // Open the New Appointment form prefilled at this provider + slot. `blocked` opens it straight
  // into block-out (Closed) mode for "Block out time".
  const openCreate = (provider, start, blocked = false, durationMin = null) => {
    setPrefillDuration(durationMin) // a drag-to-size passes the painted length; a plain click passes null (form defaults)
    // Reception OVERRIDE: a dentist on leave (e.g. Dr Chalita on maternity) has a closed diary, but
    // the front desk can still book her manually — confirm, then proceed. The AI never books a
    // closed diary; this override is reception-only.
    if (provider?.on_leave && !blocked && !window.confirm(`${provider.name}'s diary is closed (on leave). Make a booking for her anyway?`)) return
    // Soft warning when placing OUTSIDE the dentist's scheduled hours (Paul 2026-07-29). Reception can
    // still override; the AI never books off-hours. Skips on-leave (already prompted) + block-out mode.
    if (!blocked && !provider?.on_leave) {
      const startMin = start instanceof Date ? start.getHours() * 60 + start.getMinutes() : null
      if (startMin != null && isOffHours(provider?.id, startMin) &&
          !window.confirm(`${provider.name} isn't scheduled to work at that time. Book anyway?`)) return
    }
    setPrefillProvider(provider)
    setPrefillStart(start)
    setSelected(null)
    setStartBlocked(blocked)
    setModalMode('create')
  }

  // SINGLE-CLICK = just SELECT the slot (Ctrl+V paste target + highlight). It no longer opens a
  // dialog — creating is now double-click or right-click (the Open Dental / Outlook pattern), so a
  // stray click can't pop a form (which made Ctrl+C / Ctrl+V awkward).
  const selectColumnSlot = (provider, ev) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return } // this click is the tail of a drag-create
    const slot = slotFromEvent(ev)
    if (!slot) return
    lastSlotRef.current = { provider, start: slot.start }
    setSelectedSlot({ providerId: provider.id, startMin: slot.startMin })
  }

  // DRAG on EMPTY diary space to carve out a slot of the dragged LENGTH — including inside a dentist's
  // not-working time — so reception can make e.g. a 45-minute new-patient slot for Dr Chalita on a day she
  // doesn't normally work (Paul 2026-08-20). Press-drag paints the block; release opens the New Appointment
  // form pre-filled with that start + duration. A plain click (no drag) still just selects the slot, and a
  // double-click still opens the form — the drag only kicks in once the pointer actually moves ≥ one slot.
  const startCreateDrag = (provider, ev) => {
    if (readOnly || ev.button !== 0) return
    if (!ev.target.hasAttribute('data-col')) return // ONLY on truly empty space — never on an appointment/block
    const colEl = ev.currentTarget
    const startSlot = slotFromEvent(ev); if (!startSlot) return
    createDragRef.current = { provider, startMin: startSlot.startMin, moved: false }
    const minAt = (clientY) => {
      const rect = colEl.getBoundingClientRect()
      const m = DAY_START + Math.round(((clientY - rect.top) / PX) / 15) * 15
      return Math.min(Math.max(m, DAY_START), DAY_END)
    }
    const onMove = (e) => {
      const s = createDragRef.current; if (!s) return
      const end = Math.max(minAt(e.clientY), s.startMin + 15)
      if (end - s.startMin >= 30) s.moved = true // a real drag = at least one full slot past the start
      setCreateDrag({ providerId: s.provider.id, startMin: s.startMin, endMin: end })
    }
    const onUp = (e) => {
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp)
      const s = createDragRef.current; createDragRef.current = null
      setCreateDrag(null)
      if (s && s.moved) {
        suppressClickRef.current = true
        const durationMin = Math.max(minAt(e.clientY), s.startMin + 15) - s.startMin
        const startDate = new Date(date + 'T00:00:00'); startDate.setMinutes(s.startMin)
        openCreate(s.provider, startDate, false, durationMin)
      }
    }
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp)
  }

  // DOUBLE-CLICK = open the New Appointment form at this slot (the deliberate create gesture).
  // openCreate handles the on-leave override confirm.
  const openCreateAtSlot = (provider, ev) => {
    if (readOnly) return
    const slot = slotFromEvent(ev)
    if (!slot) return
    lastSlotRef.current = { provider, start: slot.start }
    openCreate(provider, slot.start, false)
  }

  // RIGHT-CLICK on empty space = context menu (New appointment · Block out time · Paste here).
  const openSlotMenu = (provider, ev) => {
    if (readOnly) return
    if (ev.target.closest('[data-block]')) return // block right-click is handled on the block itself
    ev.preventDefault()
    const slot = slotFromEvent(ev)
    if (!slot) return
    lastSlotRef.current = { provider, start: slot.start }
    setSelectedSlot({ providerId: provider.id, startMin: slot.startMin })
    setSlotMenu({ x: ev.clientX, y: ev.clientY, provider, start: slot.start })
  }

  // ── Drag-to-move: drag an Ivory appointment to a new time and/or dentist ──
  // Elixir-parity reschedule. Keeps the duration; the dentist = the column you
  // drop on. Server enforces the per-provider no-overlap constraint, so a drop
  // onto a taken slot is rejected with a friendly message.
  const dragRef = useRef(null)
  const [dragId, setDragId] = useState(null)

  // ── Resize: drag the bottom edge of an appointment to change its end time ──
  const resizeRef = useRef(null)
  const [resize, setResize] = useState(null) // live render: { id, startMin, endMin }
  const startResize = (e, a) => {
    e.preventDefault(); e.stopPropagation()
    const info = {
      id: a.id,
      startMin: minsFromMidnight(a.start_time),
      origEndMin: minsFromMidnight(a.end_time),
      endMin: minsFromMidnight(a.end_time),
      startY: e.clientY,
    }
    resizeRef.current = info
    setResize({ id: info.id, startMin: info.startMin, endMin: info.endMin })
    const onMove = (ev) => {
      const i = resizeRef.current; if (!i) return
      let endMin = Math.round((i.origEndMin + (ev.clientY - i.startY) / PX) / 15) * 15
      endMin = Math.min(Math.max(endMin, i.startMin + 15), DAY_END)
      if (endMin !== i.endMin) { i.endMin = endMin; setResize({ id: i.id, startMin: i.startMin, endMin }) }
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp)
      const i = resizeRef.current; resizeRef.current = null; setResize(null)
      if (i && i.endMin !== i.origEndMin) {
        // Send BOTH times — the controller rejects an end-only change (needs start + end together).
        const start = new Date(date + 'T00:00:00'); start.setMinutes(i.startMin)
        const end = new Date(date + 'T00:00:00'); end.setMinutes(i.endMin)
        // A resize keeps the start time, but if this appointment already sits off-hours the #update guard
        // would still reject the length change — pass the override so a staff resize is never a dead "no".
        router.patch(`/appointments/${i.id}`, { appointment: { start_time: start.toISOString(), end_time: end.toISOString() }, override_off_hours: true }, {
          preserveScroll: true, preserveState: true,
          onError: () => window.alert('Could not change the appointment length.'),
        })
      }
    }
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp)
  }

  // Drag an appointment onto the Cancelled panel to cancel it (status -> cancelled).
  const [cancelDragOver, setCancelDragOver] = useState(false)
  // The Cancelled list is HIDDEN by default (Paul 2026-06-25 — patients in the chair can see the
  // diary, and cancelled names shouldn't be on show). Reveal it via the header "Cancelled" button;
  // it also auto-reveals while dragging an appointment so drag-to-cancel still works.
  const [showCancelled, setShowCancelled] = useState(false)
  // Once revealed, the strip drops away again after 10s (Paul 2026-06-25) so it doesn't linger on
  // screen. Hovering over it pauses the countdown so reception can read/act without it vanishing.
  const cancelHideTimer = useRef(null)
  const armCancelAutoHide = () => { clearTimeout(cancelHideTimer.current); cancelHideTimer.current = setTimeout(() => setShowCancelled(false), 10_000) }
  const pauseCancelAutoHide = () => clearTimeout(cancelHideTimer.current)
  useEffect(() => {
    if (!showCancelled) { clearTimeout(cancelHideTimer.current); return undefined }
    armCancelAutoHide()
    return () => clearTimeout(cancelHideTimer.current)
  }, [showCancelled])
  const onCancelDrop = (e) => {
    const info = dragRef.current
    setCancelDragOver(false)
    if (!info) return
    e.preventDefault()
    onApptDragEnd()
    openCancel(appointments.find((a) => a.id === info.id) || { id: info.id, patient_name: info.title })   // ask WHY + WHO first (Paul 2026-07-03)
  }

  // ── Right-click context menu (status / cancel / edit) ────────────────
  // { x, y, appt } while open; null when closed. Acts ONLY on Ivory blocks.
  const [ctxMenu, setCtxMenu] = useState(null)
  // The currently-selected appointment (single click) — highlighted, and the
  // target for Ctrl+C / Ctrl+X.
  const [selectedId, setSelectedId] = useState(null)
  const onApptContextMenu = (e, a) => {
    if (readOnly) return
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ x: e.clientX, y: e.clientY, appt: a })
  }
  const onCtxPick = (key) => {
    const appt = ctxMenu?.appt
    setCtxMenu(null)
    if (!appt) return
    if (key === 'cancel') { openCancel(appt); return }   // ask WHY + WHO first (Paul 2026-07-03)
    if (key === 'delete') { deleteAppointment(appt); return }
    if (key === 'edit') { openEdit(appt); return }
    // A patient-journey status change — recolours server-side.
    router.patch(`/appointments/${appt.id}/set_status`, { status: key }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => toast.success(STATUS_TOAST[key] || 'Status updated'),
      onError: () => toast.error('Could not update'),
    })
  }

  // ── Copy & paste (Ctrl/Cmd+C → duplicate, Ctrl/Cmd+V → place) ─────────
  // `lastApptRef` = the most recently clicked Ivory block; `lastSlotRef` =
  // the last empty slot clicked (provider + snapped start). `clipboard`
  // holds what was copied so a banner can show and Ctrl+V can duplicate it.
  const lastApptRef = useRef(null)
  const lastSlotRef = useRef(null)
  // Clipboard persists in sessionStorage so a copied/cut appointment SURVIVES day
  // navigation. prev/next + the date picker do a full Inertia visit (goTo) that
  // remounts this page, which used to reset useState(null) and silently drop the
  // clipboard — so you could only paste within the SAME day you copied. Backing it
  // with sessionStorage lets you copy on one day, move to another, click a slot and
  // Ctrl+V. Cleared on paste-move success / Esc / the banner ✕ (same as before).
  const CLIPBOARD_KEY = 'diary:clipboard'
  const [clipboard, setClipboardState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(CLIPBOARD_KEY)) || null } catch { return null }
  })
  const setClipboard = (value) => {
    setClipboardState(value)
    try {
      if (value) sessionStorage.setItem(CLIPBOARD_KEY, JSON.stringify(value))
      else sessionStorage.removeItem(CLIPBOARD_KEY)
    } catch { /* sessionStorage unavailable — clipboard just won't persist across days */ }
  }

  // Place the copied/cut appointment at a slot ({ provider, start }). Shared by Ctrl+V and the
  // right-click "Paste here". Cut = MOVE the original; copy = DUPLICATE for the same patient.
  const doPaste = (slot) => {
    if (!clipboard || !slot) return
    if (slot.provider?.on_leave && !window.confirm(`${slot.provider.name}'s diary is closed (on leave). Place this here anyway?`)) return
    // Off-hours: confirm ONCE on-screen, then tell the SERVER the person confirmed (override_off_hours) —
    // otherwise the #update off-hours guard rejects the confirmed paste and the staff member just sees a
    // dead "no" (Paul 2026-08-16: "I want to book the patient for 4pm manually. Stop saying no."). A person
    // can always place a booking off-hours here; the confirm is the hard-confirm.
    let offHours = false
    if (!slot.provider?.on_leave) {
      const sm = new Date(slot.start).getHours() * 60 + new Date(slot.start).getMinutes()
      offHours = isOffHours(slot.provider?.id, sm)
      if (offHours && !window.confirm(`${slot.provider.name} isn't scheduled to work at that time. Place it here anyway?`)) return
    }
    const start = new Date(slot.start)
    const end = new Date(start.getTime() + clipboard.durationMin * 60000)
    const when = { start_time: start.toISOString(), end_time: end.toISOString(), provider_id: slot.provider.id }
    if (clipboard.cut) {
      const notify = askNotifyReschedule(clipboard.patient_name)
      router.patch(`/appointments/${clipboard.apptId}`, { appointment: when, override_off_hours: offHours, notify_reschedule: notify }, {
        preserveScroll: true, preserveState: true,
        onSuccess: () => { toast.success(`Moved ${clipboard.patient_name}`); setClipboard(null) },
        onError: () => toast.error('Could not move — that slot may be taken'),
      })
    } else {
      router.post('/appointments', { appointment: { ...when, patient_id: clipboard.patient_id, reason: clipboard.reason } }, {
        preserveScroll: true, preserveState: true,
        onSuccess: () => toast.success(`Pasted ${clipboard.patient_name}`),
        onError: () => toast.error('Could not paste — that slot may be taken'),
      })
    }
  }

  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack copy/paste while typing in an input (e.g. the date picker).
      const tag = (e.target?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return
      const mod = e.ctrlKey || e.metaKey
      // Delete / Backspace (no modifier) = permanently REMOVE the last-clicked block from the diary.
      if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) {
        const a = lastApptRef.current
        if (a) { e.preventDefault(); deleteAppointment(a) }
        return
      }
      // Ctrl+C = copy (duplicate on paste) · Ctrl+X = cut (move on paste).
      if (mod && (e.key === 'c' || e.key === 'C' || e.key === 'x' || e.key === 'X')) {
        const a = lastApptRef.current
        if (!a) return
        e.preventDefault()
        const cut = e.key === 'x' || e.key === 'X'
        const durationMin = Math.max(
          Math.round((new Date(a.end_time) - new Date(a.start_time)) / 60000), 15,
        )
        setClipboard({ apptId: a.id, patient_id: a.patient_id, patient_name: a.patient_name, reason: a.reason, durationMin, cut })
        toast(`${cut ? 'Cut' : 'Copied'} ${a.patient_name} — click a slot and Ctrl+V`)
      } else if (mod && (e.key === 'v' || e.key === 'V')) {
        if (!clipboard || !lastSlotRef.current) return
        e.preventDefault()
        doPaste(lastSlotRef.current)
      } else if (e.key === 'Escape') {
        if (clipboard) setClipboard(null)
        setSelectedSlot(null)
        setSlotMenu(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clipboard])

  const onApptDragStart = (e, a) => {
    if (resizeRef.current) { e.preventDefault(); return }  // grabbing the resize handle, not moving
    setShowCancelled(true) // reveal the Cancelled drop zone while dragging so drag-to-cancel still works
    const startMin = minsFromMidnight(a.start_time)
    const endMin = minsFromMidnight(a.end_time)
    const col = e.currentTarget.closest('[data-col]')
    const blockTopPx = (Math.max(startMin, DAY_START) - DAY_START) * PX
    const grabOffsetPx = col ? (e.clientY - col.getBoundingClientRect().top) - blockTopPx : 0
    dragRef.current = {
      id: a.id,
      durationMin: Math.max(endMin - startMin, 15),
      grabOffsetPx,
      origStartMin: startMin,
      origProviderId: a.provider_id,
    }
    e.dataTransfer.effectAllowed = 'move'
    try { e.dataTransfer.setData('text/plain', String(a.id)) } catch (_) {}
    setDragId(a.id)
  }
  const onApptDragEnd = () => { dragRef.current = null; setDragId(null) }
  // Ask before telling the patient about a MOVED time (Paul 2026-08-21). OK = send the new-time
  // WhatsApp · Cancel = move it quietly (don't message them). Only asked on a real time change.
  const askNotifyReschedule = (name) =>
    window.confirm(`Send ${name || 'the patient'} a WhatsApp about the NEW time?\n\nOK = Yes, tell them the new time.\nCancel = No, move it quietly (don't message them).`)

  const onColumnDragOver = (e) => { if (dragRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = 'move' } }
  const onColumnDrop = (e, provider) => {
    const info = dragRef.current
    if (!info) return
    e.preventDefault()
    onApptDragEnd()
    if (provider?.on_leave && !window.confirm(`${provider.name}'s diary is closed (on leave). Move it into her diary anyway?`)) return
    const rect = e.currentTarget.getBoundingClientRect()
    const topPx = (e.clientY - rect.top) - info.grabOffsetPx
    let startMin = DAY_START + Math.round((topPx / PX) / 15) * 15
    startMin = Math.min(Math.max(startMin, DAY_START), DAY_END - info.durationMin)
    if (startMin === info.origStartMin && provider.id === info.origProviderId) return // dropped where it was
    const offHours = !provider?.on_leave && isOffHours(provider?.id, startMin)
    if (offHours &&
        !window.confirm(`${provider.name} isn't scheduled to work at that time. Move it here anyway?`)) return
    const start = new Date(date + 'T00:00:00'); start.setMinutes(startMin)
    const end = new Date(start.getTime() + info.durationMin * 60000)
    // Only a real TIME change messages the patient — a same-time move to another dentist does not, so
    // don't ask about it. (Paul 2026-08-21)
    const notify = (startMin !== info.origStartMin) ? askNotifyReschedule(info.patient_name) : true
    router.patch(`/appointments/${info.id}`, {
      // reception confirmed the off-hours move above → tell the server it's an explicit override (Paul 2026-08-13)
      appointment: { start_time: start.toISOString(), end_time: end.toISOString(), provider_id: provider.id },
      override_off_hours: offHours,
      notify_reschedule: notify,
    }, {
      preserveScroll: true, preserveState: true,
      onError: () => window.alert('Could not move — that slot may already be taken.'),
    })
  }

  const apptsFor = (pid) => appointments.filter((a) => a.provider_id === pid)
  const elixirFor = (pid) => elixir_blocks.filter((b) => b.provider_id === pid)
  const closedFor = (pid) => closed_blocks.filter((c) => c.provider_id === pid || c.provider_id == null)
  // Non-working hours derived from the dentist's schedule (Paul 2026-07-29) — greyed, non-interactive.
  const scheduleClosedFor = (pid) => schedule_closed_blocks.filter((c) => c.provider_id === pid)
  // Is this minutes-from-midnight inside a scheduled non-working block for this provider? (soft booking warn)
  const isOffHours = (pid, startMin) =>
    scheduleClosedFor(pid).some((c) => {
      const s = minsFromMidnight(c.starts_at)
      const e = minsFromMidnight(c.ends_at)
      return startMin >= s && startMin < e
    })

  // ── Waitlist "who fits an opening" — auto-suggest (Paul 2026-06-25) ──
  // Compute the FREE gaps in the visible day (per bookable dentist) from existing appointments +
  // closed blocks. A waitlist person "fits" if any gap is at least their needed duration. When a
  // booking is cancelled its time becomes a gap, so matching people light up automatically. This is
  // a reception convenience (they phone to confirm); it ignores the lunch break, so it's a guide.
  const isToday = date === todayStr()
  const isPast = date < todayStr()
  const freeGaps = React.useMemo(() => {
    if (isPast) return []
    const nowMin = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes() })()
    const floor = isToday ? Math.max(DAY_START, Math.ceil(nowMin / 15) * 15) : DAY_START
    const gaps = []
    for (const prov of providers) {
      if (prov.on_leave || prov.accepting_bookings === false) continue
      const busy = []
      for (const a of appointments) if (a.provider_id === prov.id) busy.push([minsFromMidnight(a.start_time), minsFromMidnight(a.end_time)])
      for (const c of closed_blocks) if (c.provider_id === prov.id || c.provider_id == null) busy.push([minsFromMidnight(c.starts_at), minsFromMidnight(c.ends_at)])
      busy.sort((x, y) => x[0] - y[0])
      let cursor = floor
      for (const [s, e] of busy) {
        if (s > cursor) gaps.push({ providerId: prov.id, start: cursor, minutes: s - cursor })
        cursor = Math.max(cursor, e)
      }
      if (cursor < DAY_END) gaps.push({ providerId: prov.id, start: cursor, minutes: DAY_END - cursor })
    }
    return gaps.filter((g) => g.minutes >= 15).sort((a, b) => a.start - b.start)
  }, [appointments, closed_blocks, providers, date, isToday, isPast])

  // The earliest gap that fits this entry's duration → { start, minutes } | null.
  const fitFor = (entry) => freeGaps.find((g) => g.minutes >= (entry.duration_minutes || 30)) || null

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-brand-bg">
      <Head title={`Diary — ${fmtDayTitle(date)}`} />

      {/* Top bar: back · date nav · legend · new appointment. Wraps on a phone so no control is clipped. */}
      <header className="flex flex-shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-brand-border bg-white px-3 py-2.5 shadow-sm md:gap-4 md:px-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link href="/dashboard" className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-muted transition-colors hover:bg-brand-surface hover:text-brand-ink">
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <span className="h-5 w-px bg-brand-border" />
          {/* Three independent steppers (Paul 2026-06-26): each pair always moves by its own unit —
              Day (with Today), Week, Month — so you never switch a mode first. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="inline-flex items-center rounded-lg border border-brand-border">
              <button onClick={() => goTo(shiftDate(date, -1))} title="Previous day" aria-label="Previous day" className="rounded-l-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronLeft size={16} /></button>
              <button onClick={() => goTo(todayStr())} title="Jump to today" className="border-x border-brand-border px-2.5 py-1.5 text-sm font-medium text-brand-muted hover:bg-brand-surface hover:text-brand-ink">Today</button>
              <button onClick={() => goTo(shiftDate(date, 1))} title="Next day" aria-label="Next day" className="rounded-r-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronRight size={16} /></button>
            </div>
            <div className="inline-flex items-center rounded-lg border border-brand-border">
              <button onClick={() => goTo(shiftByUnit(date, -1, 'week'))} title="Previous week" aria-label="Previous week" className="rounded-l-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronLeft size={16} /></button>
              <span className="border-x border-brand-border px-2 py-1.5 text-xs font-semibold text-brand-muted">Week</span>
              <button onClick={() => goTo(shiftByUnit(date, 1, 'week'))} title="Next week" aria-label="Next week" className="rounded-r-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronRight size={16} /></button>
            </div>
            <div className="inline-flex items-center rounded-lg border border-brand-border">
              <button onClick={() => goTo(shiftByUnit(date, -1, 'month'))} title="Previous month" aria-label="Previous month" className="rounded-l-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronLeft size={16} /></button>
              <span className="border-x border-brand-border px-2 py-1.5 text-xs font-semibold text-brand-muted">Month</span>
              <button onClick={() => goTo(shiftByUnit(date, 1, 'month'))} title="Next month" aria-label="Next month" className="rounded-r-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink"><ChevronRight size={16} /></button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button type="button" onClick={() => setPickerOpen((o) => !o)} title="Pick a date" aria-label="Pick a date" className="inline-flex items-center rounded-lg border border-brand-border p-1.5 text-brand-primary hover:bg-brand-surface"><CalendarDays size={16} /></button>
              {pickerOpen && <MiniCalendar value={date} onPick={(d) => { goTo(d); setPickerOpen(false) }} onClose={() => setPickerOpen(false)} />}
            </div>
            <h1 className="text-base font-semibold tracking-tight text-brand-ink">{fmtDayTitle(date)}</h1>
            {providers.length > 1 && !scoped && (
              <select
                value={effectiveProviders.length === providers.length ? 'all' : viewProviderId}
                onChange={(e) => setViewProvider(e.target.value)}
                title="Show one dentist's schedule, or all dentists"
                className="ml-1 rounded-lg border border-brand-border px-2 py-1 text-xs font-medium text-brand-ink"
              >
                <option value="all">All dentists</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <DiarySearch onPick={(d) => goTo(d)} />
          <div className="hidden lg:block"><Legend /></div>
          <Link href="/appointments/calendar" title="Switch to the week view — drag appointments across days" className="inline-flex items-center gap-1.5 rounded-xl border border-brand-border px-3 py-2 text-sm font-medium text-brand-ink transition hover:bg-brand-surface/60">
            <CalendarDays size={15} /> Week view
          </Link>
          <a href={`/diary/print?date=${date}`} target="_blank" rel="noopener" title="Print the day's schedule (Elixir format) to post on the WhatsApp group" className="inline-flex items-center gap-1.5 rounded-xl border border-brand-border px-3 py-2 text-sm font-medium text-brand-ink transition hover:bg-brand-surface/60">
            <Printer size={15} /> Print schedule
          </a>
          <button onClick={() => setShowCancelled((v) => !v)} title="Show or hide today's cancellations (hidden by default so patients in the chair don't see them)" className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition ${showCancelled ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-brand-border text-brand-ink hover:bg-brand-surface/60'}`}>
            <XCircle size={15} /> Cancelled
          </button>
          {!readOnly && (
            <button onClick={() => { setSelected(null); setPrefillStart(null); setPrefillProvider(providers.find((p) => p.principal && !p.on_leave) || providers.find((p) => !p.on_leave) || providers[0] || null); setModalMode('create') }} className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-primary-dark">
              <Plus size={15} /> New appointment
            </button>
          )}
        </div>
      </header>

      {/* Diary-vs-clock nudge (Paul 2026-08-11): a patient is still In Consultation past their slot while
          the next is due — reception must Complete the previous + Start Consultation for the next so the
          scribe attaches audio to the right patient. LIVE + self-clearing (derived, refreshes on the poll);
          it disappears on its own the moment she updates the diary. Seen by reception AND Paul. */}
      {diary_nudges.length > 0 && (
        <div className="flex-shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-2 md:px-4">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-amber-600">⏱️</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Diary needs updating — the transcript is attaching to the wrong patient until you do
              </p>
              <ul className="mt-1 space-y-1">
                {diary_nudges.map((n) => (
                  <li key={n.provider_id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-900">
                    <span className="flex-1">{n.message}</span>
                    <Link href={`/appointments/${n.current_id}`} className="rounded-md bg-white/70 border border-amber-300 px-2 py-0.5 font-medium text-amber-800 hover:bg-white">Open {n.current_name}</Link>
                    {n.next_id && <Link href={`/appointments/${n.next_id}`} className="rounded-md bg-white/70 border border-amber-300 px-2 py-0.5 font-medium text-amber-800 hover:bg-white">Open {n.next_name}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <main className="min-h-0 flex-1 overflow-auto p-3">
        {providers.length === 0 ? (
          <p className="p-8 text-center text-sm text-brand-muted">No active dentists configured.</p>
        ) : (
          <div className="inline-flex min-w-full rounded-xl border border-brand-border bg-white shadow-sm">
            {/* Time gutter — sticky to the left so the hours stay visible while scrolling across dentist columns on a phone */}
            <div className="sticky left-0 z-20 w-14 flex-shrink-0 border-r border-brand-border bg-white">
              <div className="h-9 border-b border-brand-border" />
              <div className="relative" style={{ height: gridHeight }}>
                {gridLines.filter((g) => g.kind !== 'quarter').map((g) => (
                  <div
                    key={g.min}
                    className={`absolute right-1 -translate-y-1/2 tabular-nums ${g.kind === 'hour' ? 'text-[11px] font-semibold text-brand-ink' : 'text-[9px] font-normal text-brand-muted/70'}`}
                    style={{ top: (g.min - DAY_START) * PX }}
                  >
                    {fmtMin(g.min)}
                  </div>
                ))}
              </div>
            </div>

            {/* Provider columns (filtered to the selected dentist, or all) */}
            {effectiveProviders.map((prov) => (
              <div key={prov.id} className="min-w-[200px] flex-1 border-r border-brand-border last:border-r-0 md:min-w-[260px]">
                <div className="flex h-9 items-center justify-center gap-2 border-b border-brand-border bg-brand-surface/40 text-sm font-semibold text-brand-ink">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: prov.color }} />
                  {prov.name}
                  {/* Live transcribing pulse (Paul 2026-08-08): green mic = capturing, red mic-off = down.
                      Staff glance here instead of us pinging on WhatsApp when the scribe stops. */}
                  {prov.scribe_status && (
                    prov.scribe_status.live ? (
                      <span title="Transcribing — the mic is actually hearing sound (green only shows when real sound is coming through)" className="inline-flex"><Mic className="h-3.5 w-3.5 text-emerald-500" /></span>
                    ) : prov.scribe_status.reason === 'silent' ? (
                      <span title="Mic is ON but hearing NOTHING — only silence for the last few minutes. If you're mid-consultation, CHECK THE MIC: a Lark transmitter that is off, flat or unpaired shows here (the receiver's green light alone does NOT mean it's working). Green only appears once it actually hears sound." className="inline-flex animate-pulse"><Mic className="h-3.5 w-3.5 text-amber-500" /></span>
                    ) : (
                      <span title={`Transcribing DOWN (${prov.scribe_status.reason === 'no_mic' ? 'mic unplugged/wedged' : 'no signal from the surgery PC'}) — please check the surgery mic`} className="inline-flex animate-pulse"><MicOff className="h-3.5 w-3.5 text-red-500" /></span>
                    )
                  )}
                  {prov.on_leave && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">On leave</span>}
                  {/* Per-day override marker (Paul 2026-08-21): this day has one-off hours — click to revert
                      to the normal weekly template for this date. */}
                  {schedule_overrides.includes(prov.id) && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Reset ${prov.name} to normal hours on this day?`)) resetScheduleDay(prov.id) }}
                            title="This day has one-off hours — click to reset to the normal weekly schedule"
                            className="rounded-full bg-brand-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/20">
                      Custom hours · reset
                    </button>
                  )}
                </div>
                <div data-col title="Single-click: select · drag: carve out a slot of that length · double-click: new appointment · right-click: New / Block / Paste" className="relative cursor-pointer" style={{ height: gridHeight }} onMouseDown={(e) => startCreateDrag(prov, e)} onClick={(e) => selectColumnSlot(prov, e)} onDoubleClick={(e) => openCreateAtSlot(prov, e)} onContextMenu={(e) => openSlotMenu(prov, e)} onDragOver={onColumnDragOver} onDrop={(e) => onColumnDrop(e, prov)}>
                  {/* time gridlines — bold hour · lighter half-hour · faint dashed quarter (15-min step) */}
                  {gridLines.map((g) => (
                    <div key={g.min} className="pointer-events-none absolute inset-x-0" style={{
                      top: (g.min - DAY_START) * PX,
                      borderTop: g.kind === 'hour' ? '1px solid rgba(15,23,42,0.22)'
                               : g.kind === 'half' ? '1px solid rgba(15,23,42,0.12)'
                               : '1px dashed rgba(15,23,42,0.06)',
                    }} />
                  ))}

                  {/* Selected empty slot — the Ctrl+V paste target. Single-click sets this; no dialog. */}
                  {selectedSlot?.providerId === prov.id && !createDrag && (
                    <div className="pointer-events-none absolute inset-x-1 z-10 rounded-md border-2 border-dashed border-brand-primary/70 bg-brand-primary/10" style={{ top: (selectedSlot.startMin - DAY_START) * PX, height: 15 * PX }} />
                  )}
                  {/* Live drag-to-create preview: the slot being painted + its running length in minutes. */}
                  {createDrag?.providerId === prov.id && (
                    <div className="pointer-events-none absolute inset-x-1 z-20 flex items-start justify-end rounded-md border-2 border-dashed border-brand-primary bg-brand-primary/15 p-0.5" style={{ top: (createDrag.startMin - DAY_START) * PX, height: Math.max(createDrag.endMin - createDrag.startMin, 15) * PX }}>
                      <span className="rounded bg-brand-primary px-1 text-[10px] font-bold leading-none text-white">{createDrag.endMin - createDrag.startMin} min</span>
                    </div>
                  )}

                  {/* Maternity-leave / not-bookable overlay: diagonal hatch + label, blocks click-to-book */}
                  {prov.on_leave && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-6"
                         style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(180,180,180,0.10) 0, rgba(180,180,180,0.10) 10px, rgba(180,180,180,0.18) 10px, rgba(180,180,180,0.18) 20px)' }}>
                      <span className="rounded-lg bg-white/85 px-3 py-1.5 text-xs font-semibold text-brand-muted shadow-sm">Diary closed (on leave) — reception can still book by override</span>
                    </div>
                  )}

                  {/* NON-WORKING hours from the dentist's schedule (Paul 2026-07-29). Greyed, hatched,
                      non-interactive background so bookings + clicks pass through; existing appointments
                      render on top. Self-updates from Office Hours — no manual closure records. */}
                  {scheduleClosedFor(prov.id).map((c) => {
                    // A gazetted PUBLIC HOLIDAY closes the whole day for every provider (Paul 2026-07-30) —
                    // rendered as a distinct amber band labelled with the holiday name, so reception sees at
                    // a glance it's a holiday (not just off-hours). Still non-interactive: reception can book
                    // over it physically, exactly like other closed time.
                    const holiday = c.source === 'holiday'
                    return (
                    <div
                      key={c.id}
                      onClick={holiday ? undefined : (e) => { e.stopPropagation(); openScheduleEditor(prov, c) }}
                      title={holiday ? (c.note || 'Public holiday') : `${prov.name} isn't scheduled here — click to open this time on this day only (won't change other days)`}
                      className={`absolute inset-x-0 z-0 overflow-hidden border-y ${holiday ? 'pointer-events-none border-amber-300/70 bg-amber-50/80' : 'cursor-pointer border-slate-200/70 bg-slate-100/70 transition hover:bg-brand-primary/10'}`}
                      style={{ ...blockBox(c.starts_at, c.ends_at), backgroundImage: holiday ? 'repeating-linear-gradient(45deg, rgba(245,158,11,0.16) 0, rgba(245,158,11,0.16) 6px, transparent 6px, transparent 12px)' : 'repeating-linear-gradient(45deg, rgba(148,163,184,0.16) 0, rgba(148,163,184,0.16) 6px, transparent 6px, transparent 12px)' }}
                    >
                      <span className={`ml-1.5 mt-1 inline-block text-[9px] font-medium uppercase tracking-wide ${holiday ? 'text-amber-700' : 'text-slate-400'}`}>{holiday ? `🎌 ${c.note || 'Public holiday'} — closed` : 'Not working · tap to open'}</span>
                    </div>
                    )
                  })}

                  {/* Closed / blocked — click to remove */}
                  {closedFor(prov.id).map((c) => (
                    <button
                      key={`c${c.id}`}
                      type="button"
                      data-block
                      title="Click to remove this blocked time"
                      onClick={(e) => { e.stopPropagation(); removeClosed(c) }}
                      className="absolute inset-x-1 rounded-md border border-pink-300 bg-pink-100/80 px-2 py-1 text-left text-[11px] font-medium text-pink-800 transition hover:bg-pink-200/80"
                      style={blockBox(c.starts_at, c.ends_at)}
                    >
                      ⛔ {c.note || 'Closed'}
                    </button>
                  ))}

                  {/* Read-only Elixir history (faded). Click to re-book that slot. */}
                  {elixirFor(prov.id).map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      data-block
                      title="Elixir history — click to re-book this slot"
                      onClick={(e) => { e.stopPropagation(); openRebook(b) }}
                      className="absolute inset-x-1 overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-100/80 px-2 py-1 text-left text-[11px] leading-tight text-slate-600 transition hover:border-brand-primary hover:bg-slate-100"
                      style={blockBox(b.start_time, b.end_time)}
                    >
                      <div className="flex items-center justify-between font-semibold">
                        <span className="truncate">{b.patient_name}{b.account_code ? ` [${b.account_code}]` : b.is_new_patient ? ' [New]' : ''}</span>
                        <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] uppercase tracking-wide text-slate-500">Elixir</span>
                      </div>
                      {b.reason && <div className="truncate opacity-80">{b.reason}</div>}
                    </button>
                  ))}

                  {/* Ivory appointments (editable) — colour = patient-journey status.
                      Overlapping blocks are laid out SIDE BY SIDE (computeApptLanes) so both show. */}
                  {(() => {
                    const _list = apptsFor(prov.id)
                    const _lanes = computeApptLanes(_list)
                    return _list.map((a) => {
                    const _h = _lanes.get(a.id) || { left: '2px', width: 'calc(100% - 4px)' }
                    return (
                    <button
                      key={a.id}
                      data-block
                      draggable={!prov.on_leave && !readOnly}
                      onDragStart={(e) => onApptDragStart(e, a)}
                      onDragEnd={onApptDragEnd}
                      onClick={(e) => { e.stopPropagation(); lastApptRef.current = a; setSelectedId(a.id) }}
                      onDoubleClick={(e) => { e.stopPropagation(); openDetail(a) }}
                      onContextMenu={(e) => onApptContextMenu(e, a)}
                      title={`${a.patient_name} · ${a.status} — click to select (Ctrl+C copy · Ctrl+X cut · Ctrl+V paste) · double-click to open · drag to move · right-click for status`}
                      className={`group/appt absolute cursor-move overflow-hidden rounded-md px-2 py-1 text-left text-[11px] leading-tight shadow-sm transition hover:shadow-md hover:brightness-95 ${dragId === a.id ? 'opacity-40 ring-2 ring-brand-primary z-20' : selectedId === a.id ? 'ring-2 ring-brand-primary ring-offset-1 z-20' : ''}`}
                      style={{ ...blockBox(a.start_time, a.end_time), left: _h.left, width: _h.width, ...(resize?.id === a.id ? { height: Math.max((resize.endMin - resize.startMin) * PX, 20) } : {}), ...apptStyle(a), ...(dragId ? { pointerEvents: 'none' } : {}) }}
                    >
                      {/* THREE-ZONE tile (Paul 2026-08-25): LEFT = name + reason + worklist badges ·
                          MIDDLE = the estimates-to-book tracker, vertically centred · RIGHT = first-visit
                          year + drop-off radar + scribe + time. A grid with equal 1fr side columns keeps
                          the MIDDLE genuinely centred and never overlapping the text on either side. */}
                      <div className="grid h-full grid-cols-[1fr_1.9fr_auto] items-stretch gap-1">
                        {/* LEFT ── who + why + estimate worklist */}
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate font-semibold">{a.patient_name}{a.account_code ? ` [${a.account_code}]` : ''}</span>
                          {/* WHY they're coming: what reception typed as the reason (an EDIT must show at once — Paul 2026-10-13), else the intake own-words reason, else the linked estimate's title. */}
                          {(a.reason || a.patient_stated_reason || a.estimate_summary) && <span className="truncate opacity-80" title={a.reason || a.patient_stated_reason || a.estimate_summary}>{a.reason || a.patient_stated_reason || a.estimate_summary}</span>}
                          {/* POST-CONFIRMATION message flag (Paul 2026-08-25, Nicole Daniels): the patient messaged
                              AFTER confirming — the block is already orange (status_color); show what they said and a
                              ✓ tick to mark it seen (turns the block green again). The tick is a role=button (nested
                              real <button> is invalid inside the tile button) and stops propagation. */}
                          {a.post_confirm_alert && (
                            <span
                              className="mt-0.5 flex w-fit max-w-full items-center gap-1 rounded bg-white/90 px-1 py-px text-[9px] font-medium text-amber-900"
                              title={`Sent AFTER confirming — reception, please note:\n"${a.post_confirm_alert.text}"${a.post_confirm_alert.count > 1 ? `\n(+${a.post_confirm_alert.count - 1} more)` : ''}`}
                            >
                              <span className="shrink-0">💬</span>
                              <span className="truncate">after confirming: “{a.post_confirm_alert.text}”</span>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); router.patch(`/conversations/${a.post_confirm_alert.conversation_id}/ack_post_confirm`, {}, { preserveScroll: true }) }}
                                title="Mark as seen — turns the block green again"
                                className="ml-0.5 inline-flex shrink-0 cursor-pointer items-center rounded bg-emerald-600 px-1 font-bold text-white hover:bg-emerald-700"
                              >✓</span>
                            </span>
                          )}
                          {/* MANUAL-CONFIRM (Paul 2026-08-14): an undelivered WhatsApp send flags the block RED — phone them. */}
                          {a.manual_confirm_needed && (
                            <span title="A WhatsApp message could NOT be delivered to this patient — please intervene MANUALLY (phone them)." className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded bg-white/85 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-red-700">⚠️ {a.manual_intervene_reason || 'Confirm manually'}</span>
                          )}
                          {/* ESTIMATE-STATUS worklist badge (Paul 2026-07-29). */}
                          {a.estimate_status === 'needed' && (
                            <span title="This visit still needs an estimate — double-click to write it up or mark 'No estimate needed'" className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded bg-amber-400 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-black">📝 Estimate to do</span>
                          )}
                          {a.estimate_status === 'to_send' && (
                            <span title="An estimate was drafted but NOT yet sent to the patient — send it today" className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded bg-orange-400 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-black">📤 Send estimate</span>
                          )}
                          {a.estimate_status === 'done' && (
                            <span title="Estimate sent to the patient" className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded bg-white/40 px-1 py-px text-[8px] font-semibold uppercase leading-none tracking-wide">✓ Estimate sent</span>
                          )}
                          {a.estimate_status === 'case_sent' && (
                            <span title="A case bundling this patient's estimates was sent to them" className="mt-0.5 inline-flex w-fit items-center gap-0.5 rounded bg-white/40 px-1 py-px text-[8px] font-semibold uppercase leading-none tracking-wide">✓ Case of estimates sent</span>
                          )}
                        </div>

                        {/* MIDDLE ── estimates-to-book tracker (Paul 2026-08-25), WIDE + vertically centred; nil for dentists. */}
                        <div className="flex min-w-0 flex-col justify-center">
                          {a.outstanding_estimates?.length > 0 && (
                            <div
                              className="rounded bg-white/90 px-1 py-0.5 leading-tight text-brand-ink shadow-sm"
                              title={`Quoted, NOT yet booked:\n${a.outstanding_estimates.map((e) => `• ${e.title} — R${Math.round(Number(e.total)).toLocaleString('en-ZA')}`).join('\n')}`}
                            >
                              <span className="block text-[8px] font-bold uppercase tracking-wide text-amber-700">⚠ To book · {a.outstanding_estimates.length}</span>
                              {a.outstanding_estimates.slice(0, 4).map((e) => (
                                <span key={e.id} className="block truncate text-[9px] font-medium">
                                  • {e.title} <span className="font-bold text-amber-700">R{Math.round(Number(e.total)).toLocaleString('en-ZA')}</span>
                                </span>
                              ))}
                              {a.outstanding_estimates.length > 4 && (
                                <span className="block text-[8px] text-brand-muted">+{a.outstanding_estimates.length - 4} more</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* RIGHT ── first-visit year + time, then drop-off radar + scribe health, right-aligned. */}
                        <div className="flex min-w-0 flex-col items-end gap-0.5 font-semibold">
                          <span className="flex items-center gap-1">
                            <FirstVisitYear year={a.first_visit_year} />
                            <LastDentist d={a.last_invoice_dentist} />
                            <IdCheckDue due={a.id_check_due} />
                            <span className="whitespace-nowrap text-[9px] font-normal opacity-70">{fmtTime(a.start_time)}</span>
                          </span>
                          {a.first_ever_appointment && <span title="This is the patient's FIRST-EVER appointment — their first booking as a new patient. Stays here permanently so you can always see when they first came." className="rounded bg-black/75 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-white">New patient</span>}
                          {/* OWNER (Paul) drop-off radar — admin+reception only; dentists fall through to the ↩ next-booked badge. */}
                          {a.previous_appointment && <span title={`Previous appointment: ${new Date(a.previous_appointment).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}`} className="whitespace-nowrap rounded bg-white/25 px-1 py-px text-[8px] font-medium leading-none opacity-80">← {new Date(a.previous_appointment).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>}
                          {a.forward_status ? (
                            a.forward_status.state === 'booked' ? (
                              <span title={`Booked ahead: ${new Date(a.forward_status.date).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })} at ${fmtTime(a.forward_status.date)}`} className="whitespace-nowrap rounded bg-emerald-200/85 px-1 py-px text-[8px] font-bold leading-none text-emerald-900">→ {new Date(a.forward_status.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
                            ) : a.forward_status.state === 'cancelled' ? (
                              <span title={`They booked a follow-up for ${new Date(a.forward_status.date).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })} but CANCELLED it — not rebooked. Follow up.`} className="whitespace-nowrap rounded bg-red-200/90 px-1 py-px text-[8px] font-bold leading-none text-red-900">✗ cancelled {new Date(a.forward_status.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
                            ) : a.forward_status.state === 'quoted_not_booked' ? (
                              <span title="An estimate was sent on the day of this visit and they have NOT booked the quoted work — follow up." className="whitespace-nowrap rounded bg-amber-300/90 px-1 py-px text-[8px] font-bold leading-none text-amber-900">⚠ quoted · not booked</span>
                            ) : a.forward_status.state === 'need_booking' ? (
                              <span title="They were quoted for work beyond what was done on the day (a previous estimate, still not invoiced) and haven't booked it — they need a booking." className="whitespace-nowrap rounded bg-orange-400/90 px-1 py-px text-[8px] font-bold leading-none text-orange-950">⚠ need a booking</span>
                            ) : null
                          ) : (
                            a.next_appointment && <span title={`Follow-up already booked: ${new Date(a.next_appointment).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })} at ${fmtTime(a.next_appointment)}`} className="whitespace-nowrap rounded bg-white/35 px-1 py-px text-[8px] font-bold leading-none">↩ {new Date(a.next_appointment).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
                          )}
                          {/* Scribe HEALTH check (Paul 2026-08-18) — completed visit: green ✓ only if words were captured. */}
                          {a.status === 'completed' && a.scribe_capture && (
                            a.scribe_capture === 'words'
                              ? <span title="Scribe captured this visit — transcript recorded (words detected)" className="inline-flex items-center rounded-full bg-white/85 p-px"><Check className="h-3 w-3 text-emerald-600" strokeWidth={3} /></span>
                              : <span title={a.scribe_capture === 'empty' ? 'Scribe ran but captured NO words for this visit — check the surgery mic' : 'No scribe transcript for this visit — the scribe did not record'} className="inline-flex items-center rounded-full bg-white/85 p-px animate-pulse"><MicOff className="h-3 w-3 text-red-600" /></span>
                          )}
                          {/* LIVE in-chair transcribing indicator (Paul 2026-08-18). */}
                          {a.status === 'in_consultation' && a.scribe_live && (
                            a.scribe_live.state === 'capturing'
                              ? <span title={`Transcribing LIVE — capturing words for this patient (${a.scribe_live.words} words so far)`} className="inline-flex items-center gap-0.5 rounded-full bg-white/85 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-emerald-700"><Mic className="h-3 w-3 text-emerald-600" />live</span>
                              : a.scribe_live.state === 'stalled'
                                ? <span title={`No words captured after ${a.scribe_live.minutes} min in the chair — the mic may be dead. Check the surgery mic NOW.`} className="inline-flex items-center gap-0.5 rounded-full bg-white/90 px-1 py-px text-[8px] font-bold uppercase leading-none tracking-wide text-red-700 animate-pulse"><MicOff className="h-3 w-3 text-red-600" />no words</span>
                                : <span title="Scribe listening — waiting for the first words…" className="inline-flex items-center gap-0.5 rounded-full bg-white/80 px-1 py-px text-[8px] font-medium leading-none text-amber-700"><Mic className="h-3 w-3 text-amber-500" />…</span>
                          )}
                        </div>
                      </div>
                      {/* Resize handle — drag the bottom edge to lengthen/shorten (snaps to 15 min). */}
                      <div
                        onMouseDown={(e) => startResize(e, a)}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to change the appointment length"
                        className="absolute inset-x-0 bottom-0 z-10 flex h-2.5 cursor-ns-resize items-end justify-center opacity-0 transition-opacity hover:bg-black/10 group-hover/appt:opacity-100"
                      >
                        <span className="mb-0.5 h-0.5 w-5 rounded-full bg-black/40" />
                      </div>
                    </button>
                    )
                    })
                  })()}
                </div>
              </div>
            ))}

            {/* ── ASAP / cancellation list (right) — who to phone when a slot frees up (Paul 2026-06-25). ── */}
            {/* ── Letters for the treating doctor (Paul 2026-08-26) — a filed letter/report/referral shows
                 on the Dr who last saw the patient; they click OK to close it. Empty ⇒ nothing renders. ── */}
            <LettersPanel rows={doctor_letters} gridHeight={gridHeight} />

            <ToBookPanel rows={to_book} readOnly={readOnly} gridHeight={gridHeight} />

            <WaitlistPanel entries={waitlist} providers={providers} fitFor={fitFor} fmtMin={fmtMin} readOnly={readOnly} gridHeight={gridHeight} diaryDate={date} />
          </div>
        )}
      </main>

      {/* ── Cancelled (bottom strip) — HIDDEN until the header "Cancelled" button is toggled (Paul
           2026-06-25, so patients in the chair don't see cancelled names). Drag a block here to cancel. ── */}
      {showCancelled && (
        <aside
          onMouseEnter={pauseCancelAutoHide}
          onMouseLeave={armCancelAutoHide}
          onDragOver={(e) => { if (dragRef.current) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setCancelDragOver(true); pauseCancelAutoHide() } }}
          onDragLeave={() => setCancelDragOver(false)}
          onDrop={onCancelDrop}
          className={`flex flex-shrink-0 items-stretch gap-2 overflow-x-auto border-t-2 px-3 py-2.5 ${cancelDragOver ? 'border-dashed border-red-400 bg-red-50' : 'border-brand-border bg-brand-surface/30'}`}
        >
          <div className="flex flex-shrink-0 flex-col items-start justify-center gap-1 pr-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-muted">
              <XCircle size={16} /> {cancelDragOver ? 'Drop to cancel' : 'Cancelled'}
              {!cancelDragOver && cancelled_appointments.length > 0 && (
                <span className="rounded-full bg-brand-muted/20 px-1.5 text-xs font-bold">{cancelled_appointments.length}</span>
              )}
            </span>
            <button type="button" onClick={() => setShowCancelled(false)} className="text-xs font-medium text-brand-muted hover:text-brand-ink">Hide</button>
          </div>
          {cancelled_appointments.length === 0 ? (
            <p className="flex items-center px-2 text-sm text-brand-muted">{cancelDragOver ? '' : 'No cancellations today'}</p>
          ) : (
            cancelled_appointments.map((a) => (
              <div key={a.id} className="group flex w-56 flex-shrink-0 flex-col rounded-md border border-brand-border/60 bg-white px-2.5 py-2 text-sm shadow-sm">
                <div className="flex items-center justify-between gap-1.5">
                  <span className="truncate font-semibold text-brand-ink line-through opacity-70">{a.patient_name}{a.account_code ? ` [${a.account_code}]` : ''}</span>
                  <FirstVisitYear year={a.first_visit_year} />
                  <LastDentist d={a.last_invoice_dentist} />
                  <span className="ml-auto flex-shrink-0 text-xs text-brand-muted">{fmtTime(a.start_time)}</span>
                </div>
                {a.reason && <div className="truncate text-xs text-brand-muted line-through opacity-60">{a.reason}</div>}
                {a.cancellation_reason && (
                  <div className="mt-0.5 text-[11px] font-medium text-red-600">
                    ✕ {CANCEL_LABELS[a.cancellation_reason.category] || a.cancellation_reason.category || 'Cancelled'}
                    {a.cancellation_reason.by ? ` · by ${CANCEL_BY[a.cancellation_reason.by] || a.cancellation_reason.by}` : ''}
                    {a.cancellation_reason.details ? <span className="block truncate font-normal text-red-400">{a.cancellation_reason.details}</span> : null}
                  </div>
                )}
                <div className="mt-1.5 flex items-center gap-1.5">
                  <button type="button" onClick={() => restoreAppt(a)} title="Restore to the diary" className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
                    <Undo2 size={13} /> Restore
                  </button>
                  <button type="button" onClick={() => deleteAppointment(a)} title="Delete permanently" className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50">
                    <XIcon size={13} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </aside>
      )}

      {/* Copy banner — visible while an appointment is on the clipboard. */}
      {clipboard && (
        <div className="pointer-events-auto fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border border-brand-primary/40 bg-brand-primary/10 px-4 py-2 text-sm text-brand-ink shadow-lg">
          <span className="flex items-center gap-2">
            <Copy size={14} className="text-brand-primary" />
            Copied <strong>{clipboard.patient_name}</strong> — click an empty slot then press Ctrl+V to duplicate. Esc to clear.
          </span>
          <button
            type="button"
            onClick={() => setClipboard(null)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-muted hover:bg-white hover:text-brand-ink"
          >
            <XIcon size={13} /> Clear
          </button>
        </div>
      )}

      {/* Right-click status menu (Ivory blocks only). */}
      {ctxMenu && (
        <DiaryContextMenu x={ctxMenu.x} y={ctxMenu.y} onPick={onCtxPick} onClose={() => setCtxMenu(null)} />
      )}

      {/* Right-click menu on EMPTY space — New appointment · Block out time · Paste here. */}
      {slotMenu && (
        <SlotMenu x={slotMenu.x} y={slotMenu.y} canPaste={!!clipboard}
          onPick={(key) => {
            const { provider, start } = slotMenu
            setSlotMenu(null)
            if (key === 'new') openCreate(provider, start, false)
            else if (key === 'block') openCreate(provider, start, true)
            else if (key === 'paste') { lastSlotRef.current = { provider, start }; doPaste({ provider, start }) }
          }}
          onClose={() => setSlotMenu(null)} />
      )}

      {/* Modals */}
      <AppointmentDetailModal appointment={selected} open={modalMode === 'detail'} onClose={closeModal} onEdit={() => openEdit(selected)} onCancel={() => openCancel(selected)} onDelete={() => deleteAppointment(selected)} />
      <AppointmentFormModal mode="create" open={modalMode === 'create'} onClose={closeModal} patients={patients} prefillStart={prefillStart} prefillDuration={prefillDuration} diaryDate={date} providers={providers} prefillProvider={prefillProvider} startBlocked={startBlocked} />
      <AppointmentFormModal mode="edit" appointment={selected} open={modalMode === 'edit'} onClose={closeModal} providers={providers} />
      <CancelAppointmentModal appointment={selected} open={modalMode === 'cancel'} onClose={closeModal} />

      {/* Per-day schedule editor (Paul 2026-08-21): open a one-off working window on a day the dentist
          isn't normally scheduled, for THIS date only — the weekly template (every other same-weekday) is
          untouched. Same idea as editing a closed block, but per-day and reversible. */}
      <EstimateReminder date={date} appointments={appointments} cu={cu} fmtTime={fmtTime} />
      {scheduleEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setScheduleEditor(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-brand-ink">Open a time for {scheduleEditor.providerName}</h3>
            <p className="mt-1 text-xs text-brand-muted">On {date} only. This won't change {scheduleEditor.providerName}'s hours on any other day.</p>
            <div className="mt-4 flex items-center gap-3">
              <label className="flex-1 text-xs font-medium text-brand-muted">From
                <input type="time" value={scheduleEditor.start} step="900"
                       onChange={(e) => setScheduleEditor({ ...scheduleEditor, start: e.target.value })}
                       className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink" />
              </label>
              <label className="flex-1 text-xs font-medium text-brand-muted">To
                <input type="time" value={scheduleEditor.end} step="900"
                       onChange={(e) => setScheduleEditor({ ...scheduleEditor, end: e.target.value })}
                       className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink" />
              </label>
            </div>
            <div className="mt-5 flex items-center justify-between gap-2">
              {scheduleEditor.hasOverride ? (
                <button type="button" onClick={() => { if (window.confirm(`Reset ${scheduleEditor.providerName} to normal hours on this day?`)) resetScheduleDay(scheduleEditor.providerId) }}
                        className="rounded-md px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50">Reset to normal hours</button>
              ) : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={() => setScheduleEditor(null)} className="rounded-md px-3 py-1.5 text-sm font-medium text-brand-muted transition hover:bg-brand-surface">Cancel</button>
                <button type="button" onClick={submitScheduleOpen} className="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white transition hover:opacity-90">Open this time</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Letters for the treating doctor (Paul 2026-08-26). Right-hand diary panel, same shape as the ASAP
// waitlist: a filed patient letter / report / referral shows on the diary of the Dr who LAST saw that
// patient. "Open" views the PDF; "OK" stamps it seen and drops it off. A dentist sees only their own
// patients' letters; the admin sees all. Empty ⇒ the panel does not render.
function LettersPanel({ rows = [], gridHeight }) {
  if (!rows.length) return null
  // triage: 'action' = "Follow up" on a specialist letter that needs a decision (specialistletter1013, Paul 2026-10-13).
  const markSeen = (id, triage) =>
    router.patch(`/documents/${id}/mark_letter_seen`, triage ? { triage } : {}, { preserveScroll: true, onSuccess: () => toast.success(triage ? 'Cleaning follow-up queued' : 'Letter marked read') })
  return (
    <aside className="hidden w-64 flex-shrink-0 flex-col border-l-2 border-brand-border bg-sky-50/40 md:flex md:w-72">
      <div className="flex items-center gap-2 border-b border-brand-border px-3 py-2 text-sm font-semibold text-brand-ink">
        <FileText size={16} className="text-sky-600" /> Letters to read
        <span className="rounded-full bg-sky-500/20 px-1.5 text-xs font-bold text-sky-700">{rows.length}</span>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2" style={{ maxHeight: gridHeight }}>
        <p className="px-1 text-[11px] leading-tight text-brand-muted">New patient letters filed for your patients — open, then tap OK to close.</p>
        {rows.map((r) => (
          <div key={r.document_id} className="rounded-md border border-sky-200 bg-white px-2.5 py-2 text-sm shadow-sm">
            <div className="flex items-center justify-between gap-1.5">
              <span className="truncate font-semibold text-brand-ink">{r.patient_name}</span>
              <span className="ml-auto flex-shrink-0 text-xs text-brand-muted">{r.filed_on}</span>
            </div>
            <div className="mt-0.5 truncate text-[12px] text-brand-muted" title={r.title}>{r.title}</div>
            {r.provider && <div className="text-[10px] text-brand-muted">{r.provider}</div>}
            {r.triage_label && (
              <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-px text-[10px] font-semibold text-amber-800">{r.triage_label}</span>
            )}
            <div className="mt-1.5 flex items-center gap-1.5">
              <a href={`/documents/${r.document_id}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-0.5 rounded bg-sky-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-sky-700">
                Open
              </a>
              {r.triage_status === 'needs_review' ? (
                <>
                  <button type="button" onClick={() => markSeen(r.document_id, 'action')}
                    className="inline-flex items-center gap-0.5 rounded border border-sky-300 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-100">
                    Follow up
                  </button>
                  <button type="button" onClick={() => markSeen(r.document_id)}
                    className="inline-flex items-center gap-0.5 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                    <Check size={12} /> OK, info only
                  </button>
                </>
              ) : (
              <button type="button" onClick={() => markSeen(r.document_id)}
                className="inline-flex items-center gap-0.5 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                <Check size={12} /> OK
              </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

// ── TO BOOK TODAY ────────────────────────────────────────────────────────────
//
// Who walked out of the chair today without a date in the diary (Paul 2026-09-11). The ASAP waitlist
// answers "who can fill a gap"; this answers the more expensive question — "who was just here, was
// told they need more work, and has nothing booked". That window is an hour or two wide, and the
// estimates tracker only notices it weeks later.
//
// A dentist sees the patients THEY treated, because "did the doctor ask them back" is their question;
// their copy carries no rand values, the same as every other screen.
//
// Saying why writes through PATCH /estimates/patient_tracking — the SAME endpoint the estimates
// tracker uses — so a reason captured here at the desk, while it is still known, is the reason the
// tracker shows weeks later. It is never retyped and the two screens cannot drift.
//
// 🚨 A HANDLED NAME LEAVES THE LIST. The panel is a worklist for today, not a record. A row that
// lingers after it has been dealt with is how a list stops being read. It disappears the moment the
// reason saves, and the reason lives on in the tracker.
const NOT_BOOKED_REASONS = [
  ['cost', 'Cost'],
  ['thinking', 'Wants to think'],
  ['scheduling', 'Timing / away'],
  ['medical_aid', 'Medical aid first'],
  ['second_opinion', 'Second opinion'],
  ['anxiety', 'Anxious'],
  ['will_call_back', 'Will call back'],
  ['recontact_later', 'Contact again later'],
  ['declined', 'Declined'],
  // Paul 2026-09-29 (Monica): "when the reason is something else, the name stays and what I type doesn't
  // save" — there was no Other, and a typed reason with no chip never submitted. Other + a Save button.
  ['other', 'Other'],
]

// The same delays the estimates tracker offers, so a date set here means the same thing there.
const DELAYS = [
  ['2 days', 2],
  ['7 days', 7],
  ['2 weeks', 14],
  ['4 weeks', 28],
  ['3 months', 90],
]

// "Declined" is the one reason that does not imply a next contact — everything else is a patient we
// mean to go back to, so the panel asks when.
const NEEDS_A_DATE = (reason) => reason !== 'declined'

function ToBookPanel({ rows = [], readOnly, gridHeight }) {
  const [asking, setAsking] = useState(null)     // patient_id whose picker is open
  const [reason, setReason] = useState(null)     // the reason chosen, waiting on a date
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [handled, setHandled] = useState(() => new Set())  // gone from the list straight away

  const visible = rows.filter((r) => !handled.has(r.patient_id))
  if (!visible.length) return null

  const close = () => { setAsking(null); setReason(null); setNote('') }

  const save = (row, pickedReason, delayDays) => {
    setSaving(true)
    const tracking = { not_booked_reason: pickedReason, not_booked_reason_note: note.trim() || null }
    if (delayDays != null) {
      const d = new Date()
      d.setDate(d.getDate() + delayDays)
      tracking.next_follow_up_at = d.toISOString().slice(0, 10)
    }
    router.patch('/estimates/patient_tracking', {
      patient_id: row.patient_id,
      estimate_ids: row.estimate_ids,
      tracking,
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setHandled((prev) => new Set(prev).add(row.patient_id))
        toast.success(delayDays != null
          ? 'Saved — back on the tracker in ' + delayDays + ' days'
          : 'Saved — it shows on the estimates tracker')
        close()
      },
      onError: () => toast.error('Could not save that'),
      onFinish: () => setSaving(false),
    })
  }

  const sinceText = (iso) => {
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return mins + ' min ago'
    const h = Math.floor(mins / 60)
    return h < 24 ? h + 'h ' + (mins % 60) + 'm ago' : 'earlier'
  }

  return (
    <aside className="hidden w-64 flex-shrink-0 flex-col border-l-2 border-brand-border bg-amber-50/50 md:flex md:w-72">
      <div className="flex items-center gap-2 border-b border-brand-border px-3 py-2.5">
        <CalendarPlus size={15} className="text-amber-700" />
        <h2 className="text-sm font-semibold text-brand-ink">To book</h2>
        <span className="rounded-full bg-amber-200 px-1.5 py-px text-[11px] font-bold text-amber-900">{visible.length}</span>
      </div>
      <p className="border-b border-brand-border/70 px-3 py-2 text-[11px] leading-snug text-brand-muted">
        Seen today, nothing in the diary yet. Book them, or say why not — they come off this list and
        the reason goes to the estimates tracker.
      </p>

      <div className="flex-1 overflow-y-auto" style={{ maxHeight: gridHeight }}>
        {visible.map((r) => (
          <div key={r.patient_id} className="border-b border-brand-border/60 px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <Link href={'/patients/' + r.patient_id} className="truncate text-[13px] font-semibold text-brand-ink hover:text-brand-primary">
                {r.patient_name}
              </Link>
              <span className="whitespace-nowrap text-[10px] font-medium text-amber-800">{sinceText(r.seen_at)}</span>
            </div>

            {r.asked_for && (
              <p className="mt-1 rounded border-l-2 border-emerald-500 bg-emerald-50 px-1.5 py-1 text-[11px] leading-snug text-emerald-900">
                <span className="font-semibold">Follow-up:</span> {r.asked_for}
              </p>
            )}

            {r.items && r.items.length > 0 && (
              <ul className="mt-1 space-y-px">
                {r.items.map((it) => (
                  <li key={it.id} className="truncate text-[11px] text-brand-ink/90">
                    {'• '}{it.title}
                    {it.draft && <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] font-semibold uppercase text-slate-600">draft</span>}
                    {it.total != null && <span className="ml-1 font-semibold text-amber-800">R{Math.round(it.total).toLocaleString('en-ZA')}</span>}
                  </li>
                ))}
                {r.more > 0 && <li className="text-[10px] text-brand-muted">+{r.more} more</li>}
              </ul>
            )}

            {!readOnly && (
              asking === r.patient_id ? (
                <div className="mt-1.5 rounded border border-brand-border bg-white p-1.5">
                  {!reason ? (
                    <>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Why not?</p>
                      <div className="flex flex-wrap gap-1">
                        {NOT_BOOKED_REASONS.map((opt) => (
                          <button key={opt[0]} type="button" disabled={saving}
                            onClick={() => (NEEDS_A_DATE(opt[0]) ? setReason(opt[0]) : save(r, opt[0], null))}
                            className="rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-medium text-brand-ink hover:bg-brand-surface disabled:opacity-50">
                            {opt[1]}
                          </button>
                        ))}
                      </div>
                      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Or type the reason here"
                        onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) { e.preventDefault(); setReason('other') } }}
                        className="mt-1.5 w-full rounded border border-brand-border px-1.5 py-1 text-[11px] focus:border-brand-primary focus:outline-none" />
                      {note.trim() && (
                        <button type="button" disabled={saving} onClick={() => setReason('other')}
                          className="mt-1 w-full rounded bg-brand-primary px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">
                          Save this reason
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">
                        {(NOT_BOOKED_REASONS.find((x) => x[0] === reason) || [])[1]} — contact again in
                      </p>
                      <input value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder={reason === 'other' ? 'What is the reason?' : 'Add a detail (optional)'}
                        className="mb-1.5 w-full rounded border border-brand-border px-1.5 py-1 text-[11px] focus:border-brand-primary focus:outline-none" />
                      <div className="flex flex-wrap gap-1">
                        {DELAYS.map((d) => (
                          <button key={d[1]} type="button" disabled={saving} onClick={() => save(r, reason, d[1])}
                            className="rounded border border-brand-primary/40 bg-brand-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary hover:bg-brand-primary/15 disabled:opacity-50">
                            {d[0]}
                          </button>
                        ))}
                        <button type="button" disabled={saving} onClick={() => save(r, reason, null)}
                          className="rounded border border-brand-border px-1.5 py-0.5 text-[10px] font-medium text-brand-muted hover:bg-brand-surface disabled:opacity-50">
                          No date
                        </button>
                      </div>
                    </>
                  )}
                  <button type="button" onClick={close}
                    className="mt-1 text-[10px] font-medium text-brand-muted hover:text-brand-ink">Cancel</button>
                </div>
              ) : (
                <div className="mt-1.5 flex gap-1.5">
                  <Link href={'/patients/' + r.patient_id}
                    className="rounded bg-brand-primary px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-primary-dark">
                    Book
                  </Link>
                  <button type="button" onClick={() => { setAsking(r.patient_id); setReason(null); setNote('') }}
                    className="rounded border border-brand-border bg-white px-2 py-1 text-[11px] font-medium text-brand-ink hover:bg-brand-surface">
                    Why not?
                  </button>
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </aside>
  )
}

// Cancellation list / ASAP waitlist (Paul 2026-06-25). Right-hand diary panel: people who want the
// earliest possible appointment. Reception adds walk-ins (name, phone, what they need) and phones
// them when a slot frees up; the AI auto-adds urgent/pain patients. Entries that FIT an opening in
// the visible day are highlighted green ("Opening ~HH:MM"). Nothing is auto-messaged — reception calls.
function WaitlistPanel({ entries = [], providers = [], fitFor, fmtMin, readOnly, gridHeight, diaryDate }) {
  const blank = { name: '', phone: '', reason: '', duration_minutes: 30, urgent: true, preferred_provider_id: '' }
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(blank)
  const [bookingId, setBookingId] = useState(null)   // entry being booked inline
  const [bookDate, setBookDate] = useState(diaryDate)
  const [bookTime, setBookTime] = useState('08:00')

  // 15-min start slots 06:00–17:00 for the inline "book from here" picker (early manual bookings allowed).
  const TIMES = []
  for (let m = 6 * 60; m <= 17 * 60; m += 15) TIMES.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  const fmtDone = (iso) => new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
  // After a redirect, surface the server flash (booking can fail on a guard) as the right toast.
  const flashToast = (page, okMsg) => {
    const f = page?.props?.flash
    if (f?.alert) toast.error(f.alert)
    else toast.success(f?.notice || okMsg)
  }

  const submit = (e) => {
    e.preventDefault()
    if (!form.name.trim()) { toast.error('Add a name'); return }
    router.post('/waitlist_entries', { waitlist_entry: { ...form, preferred_provider_id: form.preferred_provider_id || null } }, {
      preserveScroll: true,
      onSuccess: () => { setForm(blank); setAdding(false); toast.success('Added to the ASAP waitlist') },
      onError: () => toast.error('Could not add'),
    })
  }
  const setStatus = (entry, doVal, msg) => router.patch(`/waitlist_entries/${entry.id}`, { do: doVal }, {
    preserveScroll: true, onSuccess: () => toast.success(msg), onError: () => toast.error('Could not update'),
  })
  const crossOff = (entry) => {
    if (!window.confirm(`Cross ${entry.name} off the list? They stay visible (struck through) so you can still see they were here.`)) return
    router.delete(`/waitlist_entries/${entry.id}`, { preserveScroll: true, onSuccess: () => toast('Crossed off') })
  }
  const openBook = (entry, fit) => { setBookingId(entry.id); setBookDate(diaryDate); setBookTime(fit ? fmtMin(fit.start) : '08:00') }
  const doBook = (entry) => {
    if (!bookDate || !bookTime) { toast.error('Pick a date and time'); return }
    router.patch(`/waitlist_entries/${entry.id}/book`, { date: bookDate, time: bookTime }, {
      preserveScroll: true,
      onSuccess: (page) => { setBookingId(null); flashToast(page, `Booked ${entry.name}`) },
      onError: () => toast.error('Could not book'),
    })
  }

  const active = entries.filter((w) => w.status === 'waiting' || w.status === 'contacted')
  const done = entries.filter((w) => w.status === 'booked' || w.status === 'removed')

  return (
    <aside className="hidden w-64 flex-shrink-0 flex-col border-l-2 border-brand-border bg-brand-surface/30 md:flex md:w-72">
      <div className="flex h-10 items-center justify-center gap-1.5 border-b border-brand-border bg-brand-surface/60 text-base font-semibold text-brand-muted">
        <Bell size={16} className="text-red-500" /> ASAP waitlist
        {active.length > 0 && <span className="rounded-full bg-red-100 px-2 text-xs font-bold text-red-700">{active.length}</span>}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-2" style={{ maxHeight: gridHeight }}>
        {!readOnly && (adding ? (
          <form onSubmit={submit} className="space-y-2 rounded-md border border-brand-primary/40 bg-white p-2.5 shadow-sm">
            <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name & surname" className="w-full rounded border border-brand-border px-2 py-1.5 text-sm" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Contact number" className="w-full rounded border border-brand-border px-2 py-1.5 text-sm" />
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="What they need (challenge)" className="w-full rounded border border-brand-border px-2 py-1.5 text-sm" />
            <div className="flex items-center gap-2">
              <select value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} className="flex-1 rounded border border-brand-border px-1.5 py-1.5 text-sm">
                <option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>60 min</option><option value={90}>90 min</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-brand-ink"><input type="checkbox" checked={form.urgent} onChange={(e) => setForm({ ...form, urgent: e.target.checked })} /> Urgent</label>
            </div>
            <select value={form.preferred_provider_id} onChange={(e) => setForm({ ...form, preferred_provider_id: e.target.value })} className="w-full rounded border border-brand-border px-1.5 py-1.5 text-sm">
              <option value="">Any dentist</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div className="flex items-center gap-1.5">
              <button type="submit" className="flex-1 rounded bg-brand-primary px-2 py-1.5 text-sm font-semibold text-white hover:bg-brand-primary-dark">Add to list</button>
              <button type="button" onClick={() => { setForm(blank); setAdding(false) }} className="rounded px-2 py-1.5 text-sm text-brand-muted hover:bg-brand-surface">Cancel</button>
            </div>
          </form>
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-brand-primary/50 bg-white px-2 py-2 text-sm font-medium text-brand-primary hover:bg-brand-primary/5">
            <UserPlus size={15} /> Add someone
          </button>
        ))}

        {active.length === 0 && done.length === 0 && (
          <p className="px-2 py-6 text-center text-sm text-brand-muted">Nobody waiting</p>
        )}

        {active.map((w) => {
          const fit = fitFor ? fitFor(w) : null
          const booking = bookingId === w.id
          return (
            <div key={w.id} className={`group rounded-md border bg-white px-2.5 py-2 text-sm shadow-sm ${fit ? 'border-emerald-400 ring-1 ring-emerald-300' : 'border-brand-border/60'}`}>
              <div className="flex items-center justify-between gap-1.5">
                <span className="truncate font-semibold text-brand-ink">{w.name}</span>
                {w.urgent && <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">ASAP</span>}
              </div>
              {w.reason && <div className="truncate text-xs text-brand-muted">{w.reason}</div>}
              <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-brand-muted">
                {w.phone && <a href={`tel:${w.phone}`} className="inline-flex items-center gap-0.5 font-medium text-brand-primary hover:underline"><PhoneCall size={12} /> {w.phone}</a>}
                <span className="inline-flex items-center gap-0.5"><Clock size={12} /> {w.duration_minutes}m</span>
                {w.source === 'ai' && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">AI</span>}
                {w.status === 'contacted' && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Called</span>}
              </div>
              {fit && !booking && <div className="mt-1 inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700"><Check size={12} /> Opening ~{fmtMin(fit.start)}</div>}
              {!readOnly && !booking && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <button type="button" onClick={() => openBook(w, fit)} title="Book this person straight into the diary — no re-typing" className="inline-flex items-center gap-0.5 rounded bg-brand-primary/10 px-2 py-1 text-xs font-semibold text-brand-primary hover:bg-brand-primary/20"><CalendarDays size={13} /> Book</button>
                  <button type="button" onClick={() => setStatus(w, 'contacted', 'Marked as called')} title="Mark that you've phoned them" className="inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"><PhoneCall size={13} /> Called</button>
                  <button type="button" onClick={() => crossOff(w)} title="Cross off the list (stays visible, struck through)" className="ml-auto inline-flex items-center gap-0.5 rounded px-1.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"><XIcon size={13} /></button>
                </div>
              )}
              {!readOnly && booking && (
                <div className="mt-2 space-y-1.5 rounded border border-brand-primary/40 bg-brand-primary/5 p-2">
                  <div className="flex items-center gap-1.5">
                    <input type="date" value={bookDate} onChange={(e) => setBookDate(e.target.value)} className="min-w-0 flex-1 rounded border border-brand-border px-1.5 py-1 text-xs" />
                    <select value={bookTime} onChange={(e) => setBookTime(e.target.value)} className="rounded border border-brand-border px-1.5 py-1 text-xs">
                      {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => doBook(w)} className="flex-1 rounded bg-brand-primary px-2 py-1 text-xs font-semibold text-white hover:bg-brand-primary-dark">Book {w.duration_minutes}m</button>
                    <button type="button" onClick={() => setBookingId(null)} className="rounded px-2 py-1 text-xs text-brand-muted hover:bg-white">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {done.length > 0 && (
          <div className="pt-1">
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Booked / crossed off</p>
            <div className="space-y-1.5">
              {done.map((w) => (
                <div key={w.id} className="rounded-md border border-brand-border/40 bg-brand-surface/40 px-2.5 py-1.5 text-sm">
                  <div className="flex items-center justify-between gap-1.5">
                    <span className={`truncate font-medium line-through ${w.status === 'booked' ? 'text-emerald-700/70' : 'text-brand-muted'}`}>{w.name}</span>
                    {w.status === 'booked'
                      ? <span className="flex-shrink-0 text-[11px] font-semibold text-emerald-600">{w.booked_for ? `Booked ${fmtDone(w.booked_for)}` : 'Booked'}</span>
                      : <span className="flex-shrink-0 text-[11px] text-brand-muted">Crossed off</span>}
                  </div>
                  {w.reason && <div className="truncate text-xs text-brand-muted line-through opacity-60">{w.reason}</div>}
                  {!readOnly && <button type="button" onClick={() => setStatus(w, 'waiting', 'Restored to the list')} title="Put back on the active list" className="mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-brand-primary hover:underline"><Undo2 size={13} /> Restore</button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}

// Elixir-style diary search — type a patient name, see their bookings (upcoming first), click to
// jump the diary to that day. Debounced; read-only so it's available to view-only logins too.
function DiarySearch({ onPick }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)
  const timer = useRef(null)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return }
    clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/diary/search?q=${encodeURIComponent(q.trim())}`, { headers: { Accept: 'application/json' } })
        const data = await res.json()
        setResults(data.results || [])
        setOpen(true)
      } catch { /* ignore */ } finally { setLoading(false) }
    }, 250)
    return () => clearTimeout(timer.current)
  }, [q])

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // Jump the diary to that appointment's day; keep the dropdown open so reception can click through
  // a patient's other appointments without re-searching.
  const pick = (a) => { onPick(a.date) }

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1.5 rounded-xl border border-brand-border bg-white px-2.5 py-1.5">
        <Search size={15} className="text-brand-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search name or account no…"
          className="w-40 bg-transparent text-sm text-brand-ink placeholder:text-brand-muted focus:outline-none md:w-60"
        />
        {q && <button onClick={() => { setQ(''); setResults([]); setOpen(false) }} className="text-brand-muted hover:text-brand-ink" aria-label="Clear"><XIcon size={14} /></button>}
      </div>
      {open && (
        <div className="absolute left-0 z-40 mt-1 max-h-[30rem] w-[26rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-brand-border bg-white shadow-lg">
          {loading && <p className="px-3 py-2 text-xs text-brand-muted">Searching…</p>}
          {!loading && results.length === 0 && <p className="px-3 py-3 text-xs text-brand-muted">No patient found — try a name or account number.</p>}
          {results.map((p) => (
            <div key={p.patient_id} className="border-b border-brand-border/60 last:border-0">
              <div className="sticky top-0 flex items-center justify-between gap-2 bg-brand-surface/60 px-3 py-2">
                <span className="truncate text-sm font-semibold text-brand-ink">{p.patient_name}</span>
                {p.account_code && <span className="shrink-0 text-xs font-medium text-brand-primary">[{p.account_code}]</span>}
                <FirstVisitYear year={p.first_visit_year} />
                <LastDentist d={p.last_invoice_dentist} />
              </div>
              {p.upcoming.length === 0 && p.past.length === 0 && (
                <p className="px-3 py-2 text-xs text-brand-muted">No appointments on record.</p>
              )}
              {p.upcoming.length > 0 && <p className="px-3 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand-primary">Upcoming</p>}
              {p.upcoming.map((a) => <ApptLine key={a.id} a={a} upcoming onPick={pick} />)}
              {p.past.length > 0 && (
                <p className="px-3 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand-muted">
                  Past{p.past_total > p.past.length ? ` · showing ${p.past.length} of ${p.past_total}` : ''}
                </p>
              )}
              {p.past.map((a) => <ApptLine key={a.id} a={a} onPick={pick} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// One appointment line in the patient-search results — date + time + reason; click to jump the
// diary to that day. Upcoming highlighted, past muted.
function ApptLine({ a, upcoming, onPick }) {
  return (
    <button
      onClick={() => onPick(a)}
      className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-brand-surface/60"
    >
      <span className="min-w-0 truncate text-xs">
        <span className={`font-medium ${upcoming ? 'text-brand-primary' : 'text-brand-ink'}`}>{a.day}</span>
        <span className="text-brand-muted"> · {a.time}</span>
        {a.reason && <span className="text-brand-muted"> · {a.reason}</span>}
      </span>
      <span className="shrink-0 text-[10px] capitalize text-brand-muted">{(a.status || '').replace(/_/g, ' ')}</span>
    </button>
  )
}

// Right-click menu shown on empty diary space. Closes on the NEXT click / right-click / Escape.
function SlotMenu({ x, y, canPaste, onPick, onClose }) {
  useEffect(() => {
    const close = () => onClose()
    const onEsc = (e) => { if (e.key === 'Escape') onClose() }
    // Attach on the next tick so the very gesture that OPENED the menu (this contextmenu/click)
    // can't immediately close it. Use mousedown for outside-click (a right-click fires no 'click').
    const id = setTimeout(() => {
      window.addEventListener('mousedown', close)
      window.addEventListener('contextmenu', close)
      window.addEventListener('keydown', onEsc)
    }, 0)
    return () => { clearTimeout(id); window.removeEventListener('mousedown', close); window.removeEventListener('contextmenu', close); window.removeEventListener('keydown', onEsc) }
  }, [onClose])
  const items = [
    { key: 'new', label: 'New appointment', icon: Plus },
    { key: 'block', label: 'Block out time', icon: Ban },
  ]
  if (canPaste) items.push({ key: 'paste', label: 'Paste here', icon: Copy })
  // Keep the menu on-screen near the right/bottom edges.
  const left = Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 200)
  const top = Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : 9999) - 140)
  return (
    <div className="fixed z-50 min-w-[180px] overflow-hidden rounded-xl border border-brand-border bg-white py-1 shadow-xl" style={{ top, left }} onClick={(e) => e.stopPropagation()}>
      {items.map((it) => (
        <button key={it.key} type="button" onClick={() => onPick(it.key)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-ink transition hover:bg-brand-surface">
          <it.icon size={15} className="text-brand-muted" /> {it.label}
        </button>
      ))}
    </div>
  )
}

function Legend() {
  const items = [
    ['#ffffff', 'Booked'],
    ['#fde047', 'Awaiting confirmation'],
    ['#22c55e', 'Confirmed'],
    ['#facc15', 'Arrived'],
    ['#3b82f6', 'In chair'],
    ['#8b5cf6', 'Completed'],
    ['#f9a8d4', 'Closed'],
  ]
  return (
    <div className="hidden items-center gap-3 lg:flex">
      {items.map(([c, label]) => (
        <span key={label} className="inline-flex items-center gap-1 text-[11px] text-brand-muted">
          <span className="h-2.5 w-2.5 rounded-sm border border-slate-300" style={{ backgroundColor: c }} /> {label}
        </span>
      ))}
    </div>
  )
}
